import React, { Fragment, useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { InputTextarea } from 'primereact/inputtextarea';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Checkbox } from 'primereact/checkbox';
import { FileUpload } from 'primereact/fileupload';
import { RadioButton } from 'primereact/radiobutton';
import { Divider } from 'primereact/divider';
import { Toast } from 'primereact/toast';
import { Card } from 'primereact/card';
import { AutoComplete } from 'primereact/autocomplete';
import { ToastService } from '@iamsoftware/react-hooks';
import { CommonService } from '../../../service/CommonService';
import { CustomerService } from '../../danh-muc/khach-hang/CustomerService';
import { useCustomerDialog } from '../../../hooks/useCustomerDialog';
import { Oauth2GoogleService } from '../../hop-dong/mau-hop-dong/Oauth2GoogleService';
import { GoogleService } from '../GoogleService';
import { Menu } from 'primereact/menu';
import { ScrollPanel } from 'primereact/scrollpanel';
import Draggable from 'react-draggable';
import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist';
import { AgreementService } from 'src/service/AgreementService';
import { PdfCanvasViewer } from '../PdfCanvasViewer';
import { AgreementTemplateService } from '../../quan-ly-loai-tai-lieu/tai-lieu-mau/AgreementTemplateService';
import { Step2Signers } from './components/Step2Signers';
import { Step3Positions } from './components/Step3Positions';
import { Step1InfoAndFiles } from './components/Step1InfoAndFiles';
import { AGREEMENT_SIGNER_ROLE, SigningMode } from '../components/signing-order.types';

GlobalWorkerOptions.workerSrc = '/pdfjs/pdf.worker.min.mjs';

// Helper function to extract file extension from fileName and contentType
const extractFileExtension = (fileName?: string, contentType?: string): string => {
  // Try to extract extension from fileName
  if (fileName && typeof fileName === 'string') {
    const parts = fileName.split('.');
    if (parts.length > 1) {
      const ext = parts[parts.length - 1].toLowerCase();
      // Only use extension if it's not a number (like "123")
      if (ext && !/^\d+$/.test(ext)) {
        return ext;
      }
    }
  }

  // If no valid extension from fileName, try to get from contentType
  if (contentType) {
    if (contentType.includes('wordprocessingml')) return 'docx';
    if (contentType.includes('pdf')) return 'pdf';
    if (contentType.includes('spreadsheetml')) return 'xlsx';
    if (contentType.includes('presentationml')) return 'pptx';
    if (contentType.includes('msword')) return 'doc';
    if (contentType.includes('excel')) return 'xls';
    if (contentType.includes('powerpoint')) return 'ppt';
  }

  return 'docx';
};
type FormTypeOption = {
  label: string;
  value: string;
  enumCode?: string | null;
  raw?: any;
};

const isOrganizationCustomer = (customer: any) => {
  const partyTypeEnumId = String(customer?.partyTypeEnumId || '').toLowerCase();
  const partyTypeEnum = String(customer?.partyTypeEnum || '').toLowerCase();
  return partyTypeEnumId === 'ptyorganization' || partyTypeEnum.includes('tổ chức') || partyTypeEnum.includes('to chuc');
};

const getRecipientNameFields = (customer: any) => {
  const name = customer?.partyName || customer?.localName || customer?.label || '';
  if (isOrganizationCustomer(customer)) {
    return {
      partyName: name,
      representativeName: customer?.representativeName || customer?.fullName || ''
    };
  }

  return {
    partyName: name,
    representativeName: customer?.representativeName || customer?.fullName || ''
  };
};

const getRecipientTaxCode = (customer: any) => isOrganizationCustomer(customer) ? (customer?.partyTaxId || customer?.taxCode || '') : '';

const getRecipientIdentityCard = (customer: any) => customer?.idValue || customer?.identityCard || (!isOrganizationCustomer(customer) ? (customer?.partyTaxId || customer?.taxCode || '') : '');

export const TaiLieu: React.FC = () => {
  const navigate = useNavigate();
  const { agreementId } = useParams();
  const [searchParams] = useSearchParams();
  const fileUploadRef = useRef<FileUpload>(null);
  const effectiveDateCalendarRef = useRef<any>(null);
  const expiryDateCalendarRef = useRef<any>(null);
  const [initializing, setInitializing] = useState<boolean>(false);
  const [isViewMode, setIsViewMode] = useState<boolean>(() => searchParams.get('mode') === 'view');
  const [formData, setFormData] = useState({
    agreementName: '',
    agreementType: null,
    effectiveDate: null,
    expiryDate: null,
    agreementNo: '',
    agreementNoInt: null as number | null,
    agreementNoFormatEnumId: null as string | null,
    description: '',
    isImportant: false,
    isUrgent: false
  });

  const [attachedFiles, setAttachedFiles] = useState<any[]>([]);
  const [mainFiles, setMainFiles] = useState<any[]>([]);
  const [isValid, setIsValid] = useState(false);
  const [isFileOptionsExpanded, setIsFileOptionsExpanded] = useState(false);
  const [isAttachedFilesExpanded, setIsAttachedFilesExpanded] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [googleFileId, setGoogleFileId] = useState<string>('');
  const [showGDoc, setShowGDoc] = useState<boolean>(false);
  const [displayEditor, setDisplayEditor] = useState<any>(null);
  const [pseudoIdFormat, setPseudoIdFormat] = useState([])
  const [documentRecipients, setDocumentRecipients] = useState<any[]>([{
    _internalId: crypto.randomUUID(),
    party: null,
    taxCode: '',
    partyName: '',
    emailAddress: '',
    contactNumber: '',
    representativeName: '',
    identityCard: '',
    roleTypeId: AGREEMENT_SIGNER_ROLE,
    sequenceNum: 1,
    signingOrder: 1
  }]);
  const [signingModeEnumId, setSigningModeEnumId] = useState<SigningMode>('AgtSignSequential');
  const [hasSigner, setHasSigner] = useState<boolean>(false);
  const [signerRoleIds, setSignerRoleIds] = useState<any[]>([]);
  const [roleOptions, setRoleOptions] = useState<any[]>([]);
  const [availableRoles, setAvailableRoles] = useState<any[]>([]);
  const [customers, setCustomers] = useState<any[]>([]);
  const step2DataLoadedRef = useRef(false);
  const recipientsEnrichedRef = useRef(false);
  const [documentTypes, setDocumentTypes] = useState<any[]>([]);
  const [detailAgreementTypeOption, setDetailAgreementTypeOption] = useState<FormTypeOption | null>(null);
  const [showRecipients, setShowRecipients] = useState<boolean>(false);
  const [showSignaturePlacement, setShowSignaturePlacement] = useState<boolean>(false);
  const [selectedSigner, setSelectedSigner] = useState<any>(null);
  const [signaturePositions, setSignaturePositions] = useState<any[]>([]);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageCanvases, setPageCanvases] = useState<any[]>([]);
  const [canvasLoading, setCanvasLoading] = useState<boolean>(false);
  const [selectedSignatureType, setSelectedSignatureType] = useState<string>('AgtDigitalVerified');
  const canvasRefs = useRef<any[]>([]);
  const draggableRefs = useRef({} as any);
  const [filePreviews] = useState<{ [key: string]: string }>({});
  const [previewLoading] = useState<{ [key: string]: boolean }>({});
  const [showFileViewer, setShowFileViewer] = useState<boolean>(false);
  const [fileOperationLoading, setFileOperationLoading] = useState<boolean>(false);
  const [viewerUrl, setViewerUrl] = useState<string | null>(null);
  const [pdfGenerating, setPdfGenerating] = useState<Set<string>>(new Set());
  const [selectedFile, setSelectedFile] = useState<any>(null);
  const [pdfScale, setPdfScale] = useState<number>(1.0);
  const [pdfPages, setPdfPages] = useState<any[]>([]);
  const [pendingHash, setPendingHash] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const markDirty = () => setHasUnsavedChanges(true);
  const [showLeavePrompt, setShowLeavePrompt] = useState<boolean>(false);
  const [touchedFields, setTouchedFields] = useState<Record<string, boolean>>({});
  const [validationTriggered, setValidationTriggered] = useState(false);
  const [isTemplateMode, setIsTemplateMode] = useState<boolean>(false);
  const [templateAgreementId, setTemplateAgreementId] = useState<string | null>(null);
  const isFromTemplateAgreement = useMemo(() => isTemplateMode && !!templateAgreementId, [isTemplateMode, templateAgreementId]);
  const [activeRecipientIndex, setActiveRecipientIndex] = useState<number | null>(null);
  const toast = useRef<Toast>(null);
  const [activeMainFileIndex, setActiveMainFileIndex] = useState<number>(0);
  const [party, setParty] = useState<any>(null);
  const formatSeq = (n: number) => (n < 10 ? `0${n}` : String(n));
  const normalizeLocationSeq = (positions: any[]) =>
    positions.map((pos, idx) => ({
      ...pos,
      locationSeqId: formatSeq(idx + 1)
    }));
  const mapAgreementTypes = (payload: any): any[] => {
    const source = Array.isArray(payload)
      ? payload
      : payload?.list ?? payload?.listData ?? [];
    if (!Array.isArray(source)) return [];
    return source
      .map((item: any) => {
        if (item?.label && item?.value) {
          return item;
        }
        const value =
          item?.agreementConfigureEnumId ||
          item?.agreementTypeId ||
          item?.value ||
          '';
        const label =
          item?.agreementConfigureName ||
          item?.agreementTypeName ||
          item?.description ||
          item?.label ||
          value;
        return {
          ...item,
          value,
          label
        };
      })
      .filter((item: any) => item?.value);
  };
  const renderPage = (pdf: any, pageNumber: number) => {
    const totalPages = Number(pdf?.numPages) || 1;
    const requested = Number(pageNumber);
    const safePage = Math.min(Math.max(1, Number.isFinite(requested) ? requested : 1), totalPages);
    return pdf.getPage(safePage).then((page: any) => {
      const viewport = page.getViewport({ scale: 1.5 });
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');

      canvas.height = viewport.height;
      canvas.width = viewport.width;

      const renderContext = {
        canvasContext: context,
        viewport: viewport,
      };

      return page.render(renderContext).promise.then(() => canvas);
    });
  };

  const loadPDFToCanvas = async (file: any) => {
    if (!file) return;

    setCanvasLoading(true);
    setPageCanvases([]);
    try {
      let arrayBuffer: ArrayBuffer;
      const extension = (file.extension || extractFileExtension(file.name || file.fileName, file.type || file.contentType) || '').toLowerCase();
      const sourceFileId = file.fileId || file.googleFileId || file.id || file.agreementContentId;
      let pdfFileId = file.referenceFileId || null;

      if (!pdfFileId && sourceFileId && ['doc', 'docx'].includes(extension)) {
        console.log('loadPDFToCanvas: generating PDF preview for Word file =', sourceFileId);
        setPdfGenerating(prev => new Set(prev).add(sourceFileId));
        try {
          const pdfRes = await AgreementService.generatePdf(sourceFileId, file.name || file.fileName, file.type || file.contentType, file.agreementContentId);
          pdfFileId = pdfRes?.referenceFileId || pdfRes?.fileId || null;
          if (pdfFileId) {
            const updateReference = (current: any) => {
              const currentId = current.fileId || current.googleFileId || current.id || current.agreementContentId;
              return String(currentId) === String(sourceFileId) ? { ...current, referenceFileId: pdfFileId } : current;
            };
            setMainFiles(prev => prev.map(updateReference));
            setAttachedFiles(prev => prev.map(updateReference));
          }
        } finally {
          setPdfGenerating(prev => {
            const next = new Set(prev);
            next.delete(sourceFileId);
            return next;
          });
        }
      }

      if (pdfFileId) {
        console.log('loadPDFToCanvas: pdfFileId =', pdfFileId);
        const blob = await AgreementService.downloadPdf(pdfFileId);
        arrayBuffer = await blob.arrayBuffer();
      } else if (sourceFileId && extension === 'pdf') {
        console.log('loadPDFToCanvas: PDF sourceFileId =', sourceFileId);
        const blob = await AgreementService.downloadPdf(sourceFileId);
        arrayBuffer = await blob.arrayBuffer();
      } else if (file.googleFileId) {
        console.log('loadPDFToCanvas: fallback googleFileId =', file.googleFileId);
        if (!file.googleFileId.includes("/")) {
          await GoogleService.openAnyoneView(agreementId, file.googleFileId);
        }
        const blob = await AgreementService.downloadPdf(file.googleFileId);
        arrayBuffer = await blob.arrayBuffer();
      } else if (file.file) {
        arrayBuffer = await file.file.arrayBuffer();
      } else {
        throw new Error('No file source available');
      }

      const loadingTask = getDocument({ data: arrayBuffer });
      console.log('loadPDFToCanvas: arrayBuffer size =', arrayBuffer.byteLength);

      loadingTask.promise.then((pdf: any) => {
        console.log('loadPDFToCanvas: PDF loaded, numPages =', pdf.numPages);
        setNumPages(pdf.numPages);

        const canvasPromises = [];
        for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber++) {
          canvasPromises.push(renderPage(pdf, pageNumber));
        }

        Promise.all(canvasPromises).then((canvases: any[]) => {
          console.log('loadPDFToCanvas: canvases rendered =', canvases.length);
          setPageCanvases(canvases);
          setCanvasLoading(false);
        });
      }, (reason: any) => {
        console.error('loadPDFToCanvas: PDF load error =', reason);
        ToastService.error('Không tải được bản PDF preview để đặt vị trí ký');
        setCanvasLoading(false);
      });
    } catch (error) {
      console.error('loadPDFToCanvas: Error =', error);
      ToastService.error('Không chuẩn bị được bản PDF preview để đặt vị trí ký');
      setCanvasLoading(false);
    }
  };
  React.useEffect(() => {
    const beforeUnloadHandler = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', beforeUnloadHandler);
    return () => window.removeEventListener('beforeunload', beforeUnloadHandler);
  }, [hasUnsavedChanges]);

  React.useEffect(() => {
    let previousHash = window.location.hash;
    const onHashChange = () => {
      if (hasUnsavedChanges) {
        const target = window.location.hash;
        // revert ngay lập tức và mở dialog xác nhận
        window.location.hash = previousHash;
        setPendingHash(target);
        setShowLeavePrompt(true);
        return;
      }
      previousHash = window.location.hash;
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, [hasUnsavedChanges]);

  const handleDrop = (event: any, pageIndex: number) => {
    const canvas = canvasRefs.current[pageIndex];
    const rect = canvas?.getBoundingClientRect();
    if (!canvas || !rect) return;

    const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);
    const canvasWidth = canvas.width || rect.width;
    const canvasHeight = canvas.height || rect.height;
    const scaleX = rect.width > 0 ? canvasWidth / rect.width : 1;
    const scaleY = rect.height > 0 ? canvasHeight / rect.height : 1;
    const cursorX = (event.clientX - rect.left) * scaleX;
    const cursorY = (event.clientY - rect.top) * scaleY;

    let defaultWidth = 200;
    let defaultHeight = (selectedSignatureType === 'AgtDigitalVerified') ? 65 : 100;
    
    // Đảm bảo các loại ký tay/ký ảnh luôn là 200x100
    if (selectedSignatureType !== 'AgtDigitalVerified') {
      defaultWidth = 200;
      defaultHeight = 100;
    }
    const xMax = canvasWidth - defaultWidth;
    const yMax = canvasHeight - defaultHeight;
    const x = clamp(cursorX, 0, Math.max(0, xMax));
    const y = clamp(cursorY, 0, Math.max(0, yMax));

    const resolvePartyId = () => {
      if (!selectedSigner) return null as any;
      if (typeof selectedSigner === 'string' || typeof selectedSigner === 'number') return selectedSigner as any;
      return (selectedSigner as any).partyId || (selectedSigner as any).value || null;
    };
    const getPartyLabel = (pid: any) => {
      const r = documentRecipients.find((d: any) => d.partyId === pid);
      const raw = r?.party?.label || r?.partyName || (selectedSigner && (selectedSigner as any).label) || 'Chưa chọn người ký';
      const m = String(raw).match(/\]\s*-\s*(.+)$/);
      return m ? m[1] : String(raw);
    };
    const pid = resolvePartyId();
    const displayLabel = getPartyLabel(pid);

    // Tìm thông tin party đầy đủ từ documentRecipients
    const recipient = documentRecipients.find((r: any) =>
      r.partyId === pid ||
      r.party?.partyId === pid ||
      r.party?.value === pid
    );
    const partyInfo = recipient?.party || {};

    // Tạo signature position với đầy đủ thông tin party
    setSignaturePositions(prev => {
      const newPosition: any = {
        locationSeqId: formatSeq(prev.length + 1),
        x,
        y,
        pageIndex,
        widthPx: defaultWidth,
        heightPx: defaultHeight,
        fontSize: 12,
        signatureTypeEnumId: selectedSignatureType,
        label: displayLabel,
        partyName: recipient?.partyName || partyInfo.partyName || displayLabel,
        partyId: pid,
        internalRecipientId: recipient?._internalId || null,
        roleTypeId: AGREEMENT_SIGNER_ROLE,
        fileId: mainFiles[activeMainFileIndex]?.fileId || mainFiles[activeMainFileIndex]?.googleFileId || null,
        agreementContentId: mainFiles[activeMainFileIndex]?.agreementContentId || null
      };

      // Thêm toàn bộ thông tin party nếu có recipient
      if (recipient) {
        newPosition._internalId = recipient._internalId;
        newPosition.taxCode = recipient.taxCode || partyInfo.partyTaxId || partyInfo.taxCode || '';
        newPosition.emailAddress = recipient.emailAddress || partyInfo.emailAddress || '';
        newPosition.contactNumber = recipient.contactNumber || partyInfo.contactNumber || '';
        newPosition.representativeName = recipient.representativeName || partyInfo.representativeName || '';
        newPosition.identityCard = recipient.identityCard || partyInfo.idValue || partyInfo.identityCard || '';
        newPosition.party = {
          ...partyInfo,
          value: partyInfo.value || partyInfo.partyId || pid,
          partyId: partyInfo.partyId || pid,
          label: partyInfo.label || recipient.partyName || displayLabel
        };
      }

      return normalizeLocationSeq([...prev, newPosition]);
    });
    markDirty();
  };

  const handleDragStop = (event: any, data: any, locationSeqId: string) => {
    const updatedElements = signaturePositions.map((element) => {
      if (element.locationSeqId === locationSeqId) {
        const canvas = canvasRefs.current[element.pageIndex];
        if (!canvas) return element;

        const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);
        const canvasWidth = canvas.width || 0;
        const canvasHeight = canvas.height || 0;
        const widthPx = Number(element.widthPx || 200);
        const heightPx = Number(element.heightPx || ((element.signatureTypeEnumId === 'AgtImageVerified' || element.signatureTypeEnumId === 'AgtHandwritingVerified') ? 100 : 65));
        const maxX = canvasWidth - widthPx;
        const maxY = canvasHeight - heightPx;
        const nx = clamp(data.x, 0, Math.max(0, maxX));
        const ny = clamp(data.y, 0, Math.max(0, maxY));


        return {
          ...element,
          x: nx,
          y: ny
        };
      }
      return element;
    });
    setSignaturePositions(normalizeLocationSeq(updatedElements));
    markDirty();
  };

  const removeSignaturePosition = (locationSeqId: string) => {
    setSignaturePositions(prev => normalizeLocationSeq(prev.filter(element => element.locationSeqId !== locationSeqId)));
    markDirty();
  };

  // Sync signaturePositions with documentRecipients when recipient info changes.
  // Template positions keep their row binding by internalRecipientId/templatePartyId until a real party is selected.
  useEffect(() => {
    if (!signaturePositions.length) return;

    setSignaturePositions(prev => {
      // Filter out orphaned positions 
      const filtered = prev.filter(pos => {
        // Kiểm tra xem hàng (recipient) này còn khớp với partyId cũ không
        const rec = documentRecipients.find(r => r._internalId === pos.internalRecipientId);
        if (rec) {
          const currentPartyId = rec.partyId || rec.party?.partyId || rec.party?.value;
          if (pos.isTemplatePosition || pos.templatePartyId || rec.templatePartyId) return !!currentPartyId || !!rec.templatePartyId;
          if (pos.partyId && currentPartyId && String(pos.partyId) !== String(currentPartyId)) return false;
          if (pos.partyId && !currentPartyId) return false;
        }

        if (!pos.partyId) return false;
        const hasPartyMatch = documentRecipients.some(r => {
            const rPid = r.partyId || r.party?.partyId || r.party?.value;
            return rPid && String(rPid) === String(pos.partyId);
        });
        
        if (hasPartyMatch) return true;
        
        return false;
      });

      // Update remaining positions with new recipient info
      const updated = filtered.map(pos => {
        const recipient = documentRecipients.find((r: any) =>
          (pos.internalRecipientId && r._internalId === pos.internalRecipientId) ||
          (pos.templatePartyId && r.templatePartyId && String(r.templatePartyId) === String(pos.templatePartyId)) ||
          (pos.partyId && (String(r.partyId) === String(pos.partyId) || String(r.party?.partyId) === String(pos.partyId) || String(r.party?.value) === String(pos.partyId)))
        );
        if (!recipient) return pos;
        const partyInfo = recipient.party || {};
        const newLabel = partyInfo.label || recipient.partyName || pos.label;
        const cleanLabelMatch = String(newLabel).match(/\]\s*-\s*(.+)$/);
        const displayLabel = cleanLabelMatch ? cleanLabelMatch[1] : String(newLabel);

        return {
          ...pos,
          label: displayLabel,
          partyId: recipient.partyId || partyInfo.partyId || partyInfo.value || pos.partyId,
          templatePartyId: pos.templatePartyId || recipient.templatePartyId,
          partyName: recipient.partyName || partyInfo.partyName || pos.partyName,
          taxCode: recipient.taxCode || partyInfo.partyTaxId || pos.taxCode,
          emailAddress: recipient.emailAddress || partyInfo.emailAddress || pos.emailAddress,
          contactNumber: recipient.contactNumber || partyInfo.contactNumber || pos.contactNumber,
          representativeName: recipient.representativeName || partyInfo.representativeName || pos.representativeName,
          identityCard: recipient.identityCard || partyInfo.idValue || pos.identityCard,
          roleTypeId: AGREEMENT_SIGNER_ROLE,
          internalRecipientId: recipient._internalId || pos.internalRecipientId
        };
      });

      return normalizeLocationSeq(updated);
    });
  }, [documentRecipients]);

  // File previews are loaded on demand from the viewer/signature step, not when the edit screen opens.
  useEffect(() => {
    if (showSignaturePlacement && mainFiles.length > 0) {
      const mainFile = mainFiles[activeMainFileIndex] || mainFiles[0];
      console.log('Step3 loadPDFToCanvas mainFile:', mainFile);
      console.log('Step3 mainFiles:', mainFiles);
      loadPDFToCanvas(mainFile);
    }
  }, [showSignaturePlacement, mainFiles, activeMainFileIndex]);
  const breadcrumbItems = [
  ];
  const mapCustomerOptions = (data: any) => {
    const uniqueItems: any[] = [];
    const seenIds = new Set();
    (data?.listData || []).forEach((item: any) => {
      const id = item.partyId || item.value;
      if (id && !seenIds.has(id)) {
        seenIds.add(id);
        const { disabled, ...rest } = item;
        uniqueItems.push({ ...rest, value: id, label: `[${item.pseudoId}] - ${item.partyName || item.label}` });
      }
    });
    return uniqueItems;
  };

  const hydrateRecipientsWithCustomerDetail = async () => {
    if (recipientsEnrichedRef.current) return;
    const hasParty = documentRecipients.some((rec: any) => rec.partyId || rec.party?.partyId || rec.party?.value);
    if (!hasParty) return;

    recipientsEnrichedRef.current = true;
    const enriched = await Promise.all(documentRecipients.map(async (rec: any) => {
      const partyId = rec.partyId || rec.party?.partyId || rec.party?.value;
      if (!partyId) return rec;
      try {
        const customer = await CustomerService.get(partyId);
        const nameFields = getRecipientNameFields(customer);
        return {
          ...rec,
          taxCode: getRecipientTaxCode(customer) || rec.taxCode,
          partyName: nameFields.partyName || rec.partyName,
          emailAddress: customer.emailAddress ?? rec.emailAddress,
          contactNumber: customer.contactNumber ?? rec.contactNumber,
          representativeName: nameFields.representativeName || rec.representativeName,
          identityCard: getRecipientIdentityCard(customer) || rec.identityCard,
          partyTypeEnumId: customer.partyTypeEnumId || rec.partyTypeEnumId,
          party: {
            ...(rec.party || {}),
            value: partyId,
            partyId,
            label: `[${customer.pseudoId || partyId}] - ${customer.partyName || rec.partyName || partyId}`,
            partyTypeEnumId: customer.partyTypeEnumId || rec.party?.partyTypeEnumId
          }
        };
      } catch {
        return rec;
      }
    }));
    setDocumentRecipients(enriched);
  };

  const loadStep2Data = async () => {
    if (!step2DataLoadedRef.current) {
      step2DataLoadedRef.current = true;
      await Promise.all([
        CommonService.agreementRoleTypes?.().then((data: any) => {
          const list = data?.listData || data?.list || (Array.isArray(data) ? data : []);
          if (list.length > 0) {
            const allRoles = list.map((it: any) => ({ value: it.roleTypeId, label: it.description }));
            setRoleOptions(allRoles);
            setAvailableRoles(allRoles);
            const signerIds = list
              .filter((it: any) => {
                const desc = String(it?.description || '').toLowerCase();
                return it?.isSigner === 'Y' || /ký|sign/.test(desc) || /bên\s*[ab]/.test(desc);
              })
              .map((it: any) => it.roleTypeId);
            setSignerRoleIds(signerIds);
          }
        }).catch(() => { }),
        CustomerService.find('').then((data: any) => {
          const mapped = mapCustomerOptions(data);
          if (mapped.length > 0) setCustomers(mapped);
        }).catch(() => { })
      ]);
    }

    await hydrateRecipientsWithCustomerDetail();
  };

  React.useEffect(() => {
    CommonService.getAgreementType().then((data: any) => {
      const mappedTypes = mapAgreementTypes(data);
      setDocumentTypes(mappedTypes);
    }).catch(() => { });

  }, []);

  useEffect(() => {
    if (detailAgreementTypeOption && detailAgreementTypeOption.value) {
      setDocumentTypes(prev => {
        const exists = prev.some(item => item.value === detailAgreementTypeOption.value);
        if (exists) return prev;
        return [...prev, detailAgreementTypeOption];
      });
    }
  }, [detailAgreementTypeOption]);

  const isSignerRole = (roleTypeId: any) => roleTypeId === AGREEMENT_SIGNER_ROLE || signerRoleIds.includes(roleTypeId);

  const getRoleOptionsForRow = (rowIndex: number) => {
    const selected = documentRecipients[rowIndex]?.roleTypeId;
    const takenIds = documentRecipients
      .map((r, i) => (i === rowIndex ? null : r.roleTypeId))
      .filter((v: any) => v);
    const filtered = roleOptions.filter((o: any) => !takenIds.includes(o.value));
    if (selected && !filtered.some((o: any) => o.value === selected)) {
      const sel = roleOptions.find((o: any) => o.value === selected);
      return sel ? [sel, ...filtered] : filtered;
    }
    return filtered;
  };

  const getCustomerSuggestionsForRow = (rowIndex: number) => {
    const takenPartyIds = documentRecipients
      .map((r, i) => (i === rowIndex ? null : r.party?.value || r.party?.partyId))
      .filter((v: any) => v);
    return customers.filter((c: any) => !takenPartyIds.includes(c.value));
  };

  const addRecipient = () => {
    setDocumentRecipients(prev => [...prev, {
      _internalId: crypto.randomUUID(),
      party: null,
      taxCode: '',
      partyName: '',
      emailAddress: '',
      contactNumber: '',
      representativeName: '',
      identityCard: '',
      roleTypeId: AGREEMENT_SIGNER_ROLE,
      sequenceNum: prev.length + 1,
      signingOrder: prev.length + 1
    }]);
  };
  const removeRecipient = (idx: number) => {
    const removed = documentRecipients[idx];
    const next = documentRecipients.filter((_, i) => i !== idx);
    setDocumentRecipients(next);
    if (removed?.roleTypeId) {
      const opt = roleOptions.find((o: any) => o.value === removed.roleTypeId);
      if (opt && !availableRoles.some((o: any) => o.value === opt.value)) {
        setAvailableRoles(prev => [...prev, opt]);
      }
    }
    setHasSigner(next.some(r => isSignerRole(r.roleTypeId)));
  };
  const handleRecipientChange = async (idx: number, field: string, value: any) => {
    let data = [...documentRecipients];
    const oldPartyId = data[idx]?.party?.partyId || data[idx]?.party?.value || data[idx]?.partyId;
    const recipientInternalId = data[idx]?._internalId;
    const templatePartyId = data[idx]?.templatePartyId;
    data[idx][field] = value;
    data[idx].roleTypeId = AGREEMENT_SIGNER_ROLE;
    if (field === 'party') {
      // Reset thông tin liên quan khi đổi đối tượng
      data[idx].taxCode = '';
      data[idx].partyName = '';
      data[idx].emailAddress = '';
      data[idx].contactNumber = '';
      data[idx].representativeName = '';
      data[idx].identityCard = '';
      data[idx].partyId = value?.partyId || value?.value || null;

      // Xóa vị trí ký của đối tượng cũ khi thay đổi đối tượng
      const newPartyId = value?.partyId || value?.value || null;
      if ((templatePartyId && newPartyId) || (oldPartyId && String(oldPartyId) !== String(newPartyId || ''))) {
        if (templatePartyId) {
          setSignaturePositions(prev => normalizeLocationSeq(prev.map(pos => {
            const isTemplatePositionForRow =
              (pos.internalRecipientId && pos.internalRecipientId === recipientInternalId) ||
              (pos.templatePartyId && String(pos.templatePartyId) === String(templatePartyId)) ||
              (pos.partyId && String(pos.partyId) === String(templatePartyId));
            if (!isTemplatePositionForRow) return pos;
            return {
              ...pos,
              partyId: newPartyId || '',
              templatePartyId,
              internalRecipientId: recipientInternalId,
              label: value?.label || pos.label,
              partyName: value?.partyName || value?.label || pos.partyName,
              isUnmatchedTemplatePosition: !newPartyId
            };
          })));
        } else {
          setSignaturePositions(prev =>
            normalizeLocationSeq(prev.filter(pos => {
              // Xóa theo internalRecipientId (chính xác nhất)
              if (pos.internalRecipientId && pos.internalRecipientId === recipientInternalId) {
                return false;
              }
              // Xóa theo partyId cũ nếu không có internalRecipientId
              if (!pos.internalRecipientId && pos.partyId && String(pos.partyId) === String(oldPartyId)) {
                return false;
              }
              return true;
            }))
          );
        }
      }
    }

    if (data.length > roleOptions.length) {
      data = data.slice(0, roleOptions.length);
    }
    if (field === 'party' && value?.partyId) {
      const duplicateIdx = data.findIndex((r, i) => i !== idx && (r.party?.value === value.partyId || r.party?.partyId === value.partyId));
      if (duplicateIdx !== -1) {
        ToastService.error('Đối tượng này đã được chọn ở dòng khác');
        data[idx].party = null;
        setDocumentRecipients(data);
        return;
      }
      try {
        const customer = await CustomerService.get(value.partyId);
        const nameFields = getRecipientNameFields(customer);
        data[idx].taxCode = getRecipientTaxCode(customer);
        data[idx].partyName = nameFields.partyName;
        data[idx].emailAddress = customer.emailAddress || '';
        data[idx].contactNumber = customer.contactNumber || '';
        data[idx].representativeName = nameFields.representativeName;
        data[idx].identityCard = getRecipientIdentityCard(customer);
        data[idx].partyTypeEnumId = customer.partyTypeEnumId;
        data[idx].party = {
          ...value,
          partyTypeEnumId: customer.partyTypeEnumId
        };
      } catch (e) {
      }
    }

    setDocumentRecipients(data);
    setHasSigner(data.some(r => isSignerRole(r.roleTypeId)));
  };
  const searchCustomers = (event: any) => {
    CustomerService.find(event.query).then((data: any) => {
      const uniqueItems: any[] = [];
      const seenIds = new Set();
      (data.listData || []).forEach((item: any) => {
        const id = item.partyId || item.value;
        if (id && !seenIds.has(id)) {
          seenIds.add(id);
          const { disabled, ...rest } = item;
          uniqueItems.push({ ...rest, value: id, label: `[${item.pseudoId}] - ${item.partyName || item.label}` });
        }
      });
      setCustomers(uniqueItems);
    }).catch(() => { });
  };

  const customerDialog = useCustomerDialog({
    service: CustomerService,
    requireTaxCode: true,
    async onAfterCreate(resp: any) {
      setParty(resp.partyId);
      const customerData = await CustomerService.find('');
      const uniqueItems: any[] = [];
      const seenIds = new Set();
      (customerData.listData || []).forEach((customer: any) => {
        const id = customer.partyId || customer.value;
        if (id && !seenIds.has(id)) {
          seenIds.add(id);
          const { disabled, ...rest } = customer;
          uniqueItems.push({ ...rest, value: id, label: `[${customer.pseudoId}] - ${customer.partyName || customer.label}` });
        }
      });
      setCustomers(uniqueItems);
      const newCustomer = uniqueItems.find((customer: any) => customer.partyId === resp.partyId);
      if (newCustomer && activeRecipientIndex !== null) {
        await handleRecipientChange(activeRecipientIndex, 'party', {
          value: newCustomer.partyId,
          partyId: newCustomer.partyId,
          label: newCustomer.label
        });
      }
    }
  });

  const createCustomer = (idx: number) => {
    console.log('>>> TaiLieu/index.tsx createCustomer click idx:', idx);
    setActiveRecipientIndex(idx);
    const rec = idx >= 0 ? documentRecipients[idx] : null;
    console.log('>>> current data for pre-fill:', rec);
    customerDialog.create({
      partyTypeEnumId: (rec && rec.taxCode) ? 'PtyOrganization' : 'PtyPerson',
      partyName: rec?.partyName || '',
      emailAddress: rec?.emailAddress || '',
      contactNumber: rec?.contactNumber || '',
      taxCode: rec?.taxCode || '',
      idValue: rec?.identityCard || ''
    });
  };

  const validateRecipients = (): boolean => {
    if (isViewMode) return true;

    let checker = true;
    let errorMessage = '';

    for (let i = 0; i < documentRecipients.length; i++) {
      const item = documentRecipients[i];

      if (!item.party?.label) {
        checker = false;
        errorMessage = 'Chưa chọn đối tượng ký';
        break;
      }

      if (!item.emailAddress) {
        checker = false;
        errorMessage = `Chưa nhập địa chỉ email cho ${item.party?.label}`;
        break;
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(item.emailAddress)) {
        checker = false;
        errorMessage = `Địa chỉ email của ${item.party?.label} chưa nhập đúng định dạng`;
        break;
      }

      if (!item.roleTypeId) {
        checker = false;
        errorMessage = `Chưa chọn vai trò người ký cho ${item.party?.label}`;
        break;
      }

      const trimmedtaxCode = item.taxCode?.trim() || '';
      const taxCodeRegex = /^(\d{10}|\d{12}|\d{13}|\d{10}-\d{3})$/;
      if (trimmedtaxCode.length > 0 && !taxCodeRegex.test(trimmedtaxCode)) {
        checker = false;
        errorMessage = `Mã số thuế của ${item.party?.label} phải có 10, 12, 13 số `;
        break;
      }

      const trimmedContactNumber = item.contactNumber?.trim() || '';
      if (trimmedContactNumber.length > 0 && trimmedContactNumber.length !== 10) {
        checker = false;
        errorMessage = `Số điện thoại của ${item.party?.label} phải có đúng 10 số`;
        break;
      }

      const trimmedIdentityCard = item.identityCard?.trim() || '';
      if (trimmedIdentityCard.length > 0 && ![9, 12].includes(trimmedIdentityCard.length)) {
        checker = false;
        errorMessage = `CCCD/CMND của ${item.party?.label} phải có 9 hoặc 12 số`;
        break;
      }
    }

    if (!checker && errorMessage) {
      ToastService.error(errorMessage);
    }

    return checker;
  };

  useEffect(() => {
    step2DataLoadedRef.current = false;
    recipientsEnrichedRef.current = false;

    const loadDetail = async () => {
      if (!agreementId) return;
      try {
        const res = await AgreementService.getDetail(agreementId as string);
        if (res && res.success === 'Y') {
          const ag = res.agreement || {};
          const rawSigningModeEnumId = res.signingModeEnumId || ag.signingModeEnumId;
          const loadedSigningMode: SigningMode = rawSigningModeEnumId === 'AgtSignSequential' ? 'AgtSignSequential' : 'AgtSignParallel';
          setSigningModeEnumId(loadedSigningMode);

          const draftStatuses = ['AGT_DRAFT', 'AGT_INIT_DRAFT'];
          const isDraft = draftStatuses.includes(ag.statusId);
          const modeParam = searchParams.get('mode');
          const shouldViewMode = modeParam === 'view' || !isDraft;
          setIsViewMode(shouldViewMode);

          const agreementConfigureEnumId = ag.agreementConfigureEnumId || null;
          setFormData({
            agreementName: ag.agreementName || '',
            agreementType: ag.agreementConfigureEnumId || null,
            effectiveDate: ag.fromDate ? new Date(ag.fromDate) : null,
            expiryDate: ag.thruDate ? new Date(ag.thruDate) : null,
            agreementNo: ag.agreementNo || '',
            agreementNoInt: ag.agreementNoInt || null,
            agreementNoFormatEnumId: ag.agreementNoFormatEnumId || null,
            description: ag.description || '',
            isImportant: ag.isImportant === 'Y',
            isUrgent: ag.isUrgent === 'Y'
          });

          if (ag.templateAgreementId) {
            setTemplateAgreementId(ag.templateAgreementId);
            setIsTemplateMode(true);
          } else {
            setTemplateAgreementId(null);
            setIsTemplateMode(false);
          }
          if (agreementConfigureEnumId) {
            const agreementTypeLabel = ag.agreementConfigureName || ag.agreementTypeName || '';
            setDetailAgreementTypeOption({
              label: agreementTypeLabel || agreementConfigureEnumId,
              value: agreementConfigureEnumId,
              enumCode: agreementConfigureEnumId
            });
          } else {
            setDetailAgreementTypeOption(null);
          }
          setTouchedFields({});
          setValidationTriggered(false);

          const mappedMain = (res.mainFiles || []).map((f: any) => {
            const fileId = f.fileId || f.contentLocation || f.agreementContentId || f.id || null;
            const extension = getFileExtension(f.fileName) || f.contentType;
            console.log(`[loadDetail] main file: fileName=${f.fileName}, type=${f.contentType}, ext=${extension}, fileId=${fileId}, refId=${f.referenceFileId}`);

            return {
              agreementContentId: f.agreementContentId || undefined,
              name: f.fileName,
              size: 0,
              type: f.contentType,
              file: undefined,
              extension: extension,
              googleFileId: fileId,
              fileId: fileId,
              id: fileId,
              referenceFileId: f.referenceFileId,
              googleFileUrl: f.contentLocation,
              contentLocation: f.contentLocation,
              isMainFile: true
            };
          });
          const mappedAttached = (res.attachmentFiles || []).map((f: any) => {
            const fileId = f.fileId || f.contentLocation || f.agreementContentId || f.id || null;
            const extension = getFileExtension(f.fileName) || f.contentType;
            console.log(`[loadDetail] attached file: fileName=${f.fileName}, type=${f.contentType}, ext=${extension}, fileId=${fileId}, refId=${f.referenceFileId}`);

            return {
              agreementContentId: f.agreementContentId || undefined,
              name: f.fileName,
              size: 0,
              type: f.contentType,
              file: undefined,
              extension: extension,
              googleFileId: fileId,
              fileId: fileId,
              id: fileId,
              referenceFileId: f.referenceFileId || null,
              googleFileUrl: f.contentLocation,
              contentLocation: f.contentLocation,
              isMainFile: false
            };
          });

          setMainFiles(mappedMain);
          setAttachedFiles(mappedAttached);
          const apiRecipients = res.agreementParties || res.recipients || res.documentRecipients || res.parties || [];
          if (Array.isArray(apiRecipients) && apiRecipients.length > 0) {
            let normalized = apiRecipients.map((r: any, index: number) => {
              const rawSequence = Number(r.sequenceNum ?? r.signingOrder);
              const sequenceNum = loadedSigningMode === 'AgtSignSequential' && Number.isFinite(rawSequence) && rawSequence > 0 ? Math.floor(rawSequence) : (loadedSigningMode === 'AgtSignSequential' ? index + 1 : 1);
              return {
                _internalId: crypto.randomUUID(), // Gán ID nội bộ
                party: r.party || (r.partyId ? { value: r.partyId, partyId: r.partyId, label: r.partyName || r.pseudoId || r.partyId } : null),
                partyId: r.partyId,
                taxCode: r.partyTaxId || r.taxCode || '',
                partyName: r.partyName || '',
                emailAddress: r.emailAddress || r.email || '',
                contactNumber: r.contactNumber || r.phone || '',
                representativeName: r.representativeName || r.fullName || '',
                identityCard: r.identityCard || r.idValue || '',
                  roleTypeId: AGREEMENT_SIGNER_ROLE,
                  sequenceNum,
                  signingOrder: sequenceNum
                };
            });
            const finalized = normalized.map((rec: any) => {
              const label = rec.party?.label || (rec.partyName ? rec.partyName : rec.partyId);
              return {
                ...rec,
                party: rec.party && typeof rec.party === 'object'
                  ? { value: rec.party.value || rec.party.partyId || rec.partyId, partyId: rec.party.partyId || rec.partyId, label }
                  : (rec.partyId ? { value: rec.partyId, partyId: rec.partyId, label } : null)
              };
            });
            setDocumentRecipients(finalized);
            var recipientListForPositions = finalized;
          }

          const apiSigners = res.signers || [];
          if ((!apiRecipients || apiRecipients.length === 0) && Array.isArray(apiSigners) && apiSigners.length > 0) {
            try {
              const mapped = apiSigners.map((s: any) => {
                const rawSequence = Number(s.sequenceNum ?? s.signingOrder);
                const sequenceNum = loadedSigningMode === 'AgtSignSequential' && Number.isFinite(rawSequence) && rawSequence > 0 ? Math.floor(rawSequence) : 1;
                const base: any = {
                  _internalId: crypto.randomUUID(), // Gán ID nội bộ
                  partyId: s.partyId || '',
                  roleTypeId: AGREEMENT_SIGNER_ROLE,
                  sequenceNum,
                  signingOrder: sequenceNum,
                };
                if (s.party) {
                  const nameFields = getRecipientNameFields(s.party);
                  base.party = s.party;
                  base.partyId = s.party.partyId;
                  base.taxCode = getRecipientTaxCode(s.party);
                  base.partyName = nameFields.partyName;
                  base.emailAddress = s.party.emailAddress || '';
                  base.contactNumber = s.party.contactNumber || '';
                  base.representativeName = nameFields.representativeName;
                  base.identityCard = getRecipientIdentityCard(s.party);
                } else {
                  base.party = null;
                  base.partyName = s.partyName || '';
                  base.emailAddress = '';
                  base.contactNumber = '';
                  base.representativeName = '';
                  base.identityCard = '';
                }
                return base;
              });
              setDocumentRecipients(mapped.map((signer: any, index: number) => {
                const rawSequence = Number(signer.sequenceNum ?? signer.signingOrder);
                const sequenceNum = loadedSigningMode === 'AgtSignSequential' && Number.isFinite(rawSequence) && rawSequence > 0 ? Math.floor(rawSequence) : (loadedSigningMode === 'AgtSignSequential' ? index + 1 : 1);
                return {
                  ...signer,
                  roleTypeId: AGREEMENT_SIGNER_ROLE,
                  sequenceNum,
                  signingOrder: sequenceNum
                };
              }));
              recipientListForPositions = mapped; // Essential for positions mapping below
              if (!selectedSigner && mapped.length > 0) setSelectedSigner({ value: mapped[0].partyId, partyId: mapped[0].partyId, roleTypeId: mapped[0].roleTypeId, label: mapped[0].party?.label || mapped[0].partyName } as any);
            } catch {/* ignore */ }
          }
          const agreementPartiesStatus: Record<string, string> = {};
          (res?.agreementParties || res?.agreement_parties || []).forEach((p: any) => {
            if (p?.partyId) {
              agreementPartiesStatus[String(p.partyId)] = p.statusId || p.status_id || '';
            }
          });

          const apiPositions = res.signaturePositions || res.signature_positions || [];
          let mappedPositions: any[] = [];
          if (Array.isArray(apiPositions) && apiPositions.length > 0) {
            const recipientsRef = recipientListForPositions || documentRecipients;
            const getPartyLabel = (pid: any) => {
              const r = Array.isArray(recipientsRef) ? recipientsRef.find((x: any) => x.partyId === pid) : undefined;
              const raw = r?.party?.label || r?.partyName || '';
              const m = String(raw).match(/\]\s*-\s*(.+)$/);
              return m ? m[1] : String(raw);
            };
            mappedPositions = apiPositions
              .map((p: any, idx: number) => {
                const pid = p.partyId || p.party_id || null;
                const partyLabel = p.partyName || p.party_name || getPartyLabel(pid);

                // Tìm recipient tương ứng để lấy _internalId
                const rec = Array.isArray(recipientsRef)
                  ? recipientsRef.find((r: any) => String(r.partyId) === String(pid))
                  : null;

                return {
                  locationSeqId: p.locationSeqId || p.location_seq_id || String(idx + 1).padStart(2, '0'),
                  pageIndex: p.pageNumber ? Number(p.pageNumber) - 1 : Number(p.pageIndex ?? p.page_index ?? 0),
                  x: Number(p.ulx ?? p.x ?? 0),
                  y: Number(p.uly ?? p.y ?? 0),
                  widthPx: Number(p.widthPx ?? p.width_px ?? p.width ?? 200),
                  heightPx: Number(p.heightPx ?? p.height_px ?? p.height ?? 65),
                  fontSize: Number(p.fontSize ?? p.font_size ?? 12),
                  partyId: pid,
                  internalRecipientId: rec?._internalId || null,
                  roleTypeId: p.roleTypeId || p.role_type_id || null,
                  partyName: partyLabel,
                  label: partyLabel,
                  signatureTypeEnumId: p.signatureTypeEnumId || p.signature_type_enum_id || 'AgtDigitalVerified',
                  agreementContentId: p.agreementContentId || p.agreement_content_id || null,
                  fileId: p.fileId || p.file_id || null
                };
              })
              .filter((pos: any) => {
                if (!pos.partyId) return true;
                const status = agreementPartiesStatus[String(pos.partyId)];
                return status !== 'AGT_PTY_SIGNED';
              });
          }
          if (mappedPositions.length > 0) setSignaturePositions(normalizeLocationSeq(mappedPositions));
        } else {
          ToastService.error(res?.message || 'Không tải được thông tin tài liệu');
        }
      } catch (e) {
        ToastService.error(e?.message || 'Lỗi khi tải thông tin tài liệu');
      } finally { }
    };
    loadDetail();
  }, [agreementId]);
  const validateForm = () => {
    if (isViewMode) return true;
    let valid = true;
    let errorMessage = '';

    // Validate tên tài liệu
    if (!formData.agreementName || formData.agreementName.trim() === '') {
      valid = false;
      errorMessage = 'Vui lòng nhập Tên tài liệu';
    }
    // Validate loại tài liệu
    else if (formData.agreementType === null) {
      valid = false;
      errorMessage = 'Vui lòng chọn Loại tài liệu';
    }
    else if (mainFiles.length === 0) {
      valid = false;
      errorMessage = 'Vui lòng tải lên tệp tài liệu trước khi ký';
    }
    else if (!formData.agreementNo || formData.agreementNo.trim() === '') {
      valid = false;
      errorMessage = 'Vui lòng nhập Mã tài liệu';
    }
    else if (formData.effectiveDate) {
      const now = new Date();
      const effective = new Date(formData.effectiveDate as any);
      const nowPlusOneMinute = new Date(now.getTime() + 60 * 1000); // Cho phép sai số 1 phút

      if (effective.getTime() > nowPlusOneMinute.getTime()) {
        valid = false;
        errorMessage = 'Ngày hiệu lực không được lớn hơn thời điểm hiện tại';
      }
    }

    if (valid && formData.expiryDate) {
      const now = new Date();
      const expiry = new Date(formData.expiryDate as any);

      if (formData.effectiveDate) {
        const effective = new Date(formData.effectiveDate as any);
        const effectivePlusOneHour = new Date(effective.getTime() + 60 * 60 * 1000);
        const nowPlusOneHour = new Date(now.getTime() + 60 * 60 * 1000);
        const minExpiryDate = new Date(Math.max(effectivePlusOneHour.getTime(), nowPlusOneHour.getTime()));
        if (expiry.getTime() <= minExpiryDate.getTime()) {
          valid = false;
          errorMessage = 'Ngày hết hiệu lực phải lớn hơn ngày hiệu lực ít nhất 1 giờ';
        }
      } else {
        const nowPlusOneHour = new Date(now.getTime() + 60 * 60 * 1000);
        if (expiry.getTime() <= nowPlusOneHour.getTime()) {
          valid = false;
          errorMessage = 'Ngày hết hiệu lực phải lớn hơn thời điểm hiện tại ít nhất 1 giờ';
        }
      }
    }
    if (!valid && errorMessage) {
      ToastService.error(errorMessage);
    }

    setIsValid(valid);
    return valid;
  };

  const getEffectiveMaxDate = () => {
    return new Date(); // Ngày hiệu lực không được lớn hơn thời điểm hiện tại
  };

  const getExpiryMinDate = () => {
    const now = new Date();
    const minFromNow = new Date(now.getTime() + 60 * 60 * 1000);

    if (formData.effectiveDate) {
      const effective = new Date(formData.effectiveDate as any);
      const minFromEffective = new Date(effective.getTime() + 60 * 60 * 1000);
      return new Date(Math.max(minFromNow.getTime(), minFromEffective.getTime()));
    }
    return minFromNow;
  };

  const handleInputChange = (field: string, value: any, options?: { skipTouch?: boolean; skipAutoCode?: boolean }) => {
    let newData = { ...formData, [field]: value };

    if (field === 'agreementType' && formData.agreementType !== value) {
      newData = { ...newData, agreementNo: '' };
    }

    setFormData(newData);

    if (field === 'agreementType' && formData.agreementType !== value && !options?.skipAutoCode) {
      generateDocumentCode(value);
    }

    if (!options?.skipTouch) {
      setTouchedFields(prev => ({ ...prev, [field]: true }));
    }

    markDirty();
  };

  const handleCheckboxChange = (field: 'isImportant' | 'isUrgent', checked: boolean) => {
    setFormData(prev => ({ ...prev, [field]: checked }));
    markDirty();
  };

  // Chỉ xóa trên client để cập nhật UI, không gọi server
  const handleRemoveLocalFile = (file: any) => {
    const targetId = file.fileId || file.googleFileId || file.id || null;
    const matchFile = (candidate: any) => {
      const candidateId = candidate.fileId || candidate.googleFileId || candidate.id || null;
      return targetId && candidateId && targetId === candidateId;
    };

    if (file.isMainFile) {
      setMainFiles(prev => prev.filter(f => !matchFile(f)));
    } else {
      setAttachedFiles(prev => prev.filter(f => !matchFile(f)));
    }
    markDirty();
  };
  const handleFileUpload = async (event: any) => {
    const files = event.files;
    if (files && files.length > 0) {
      setFileOperationLoading(true);
      const validFiles: any[] = [];
      const invalidFiles: string[] = [];

      Array.from(files).forEach((file: any) => {
        const extension = getFileExtension(file.name);
        const allowedExtensions = ['pdf', 'docx', 'doc'];

        if (allowedExtensions.includes(extension)) {
          validFiles.push(file);
        } else {
          invalidFiles.push(file.name);
        }
      });

      if (invalidFiles.length > 0) {
        ToastService.error(`Các file sau không đúng định dạng: ${invalidFiles.join(', ')}. Chỉ hỗ trợ PDF, DOCX, DOC.`);
      }

      for (const file of validFiles) {
        try {
          const mapped = await uploadFileToGoogle(file, { isMain: true });
          if (mapped) {
            setMainFiles(prev => [...prev, mapped]);
          } else {
            ToastService.error(`Không thể upload ${file.name}`);
          }
        } catch (e) {
          ToastService.error(`Lỗi khi upload ${file.name}`);
        }

      }
      markDirty();
      setFileOperationLoading(false);
    }
  };

  const getFileExtension = (filename: string): string => {
    return filename.split('.').pop()?.toLowerCase() || '';
  };

  const uploadFileToGoogle = async (file: File, options: { isMain: boolean }) => {
    const isMain = options.isMain;
    const mapResponse = (res: any) => {
      if (!res) return null;
      const fileId = res.fileId || null;
      return {
        name: res.fileName || file.name,
        size: (file as any).size,
        type: (file as any).type,
        file: undefined,
        extension: res.format || getFileExtension(file.name),
        googleFileId: fileId,
        fileId: fileId,
        id: fileId,
        googleFileUrl: res.fileUrl,
        referenceFileId: res.referenceFileId || undefined,
        isMainFile: isMain
      };
    };

    const res = await AgreementService.uploadFile(file);
    console.log('uploadFileToGoogle response:', res);
    if (res && (res.success === 'Y' || res.fileId)) {
      // Tự động sinh PDF preview nếu là file doc/docx (chạy ngầm)
      const ext = getFileExtension(file.name);
      if (ext === 'docx' || ext === 'doc') {
        AgreementService.generatePdf(res.fileId, res.fileName || file.name, (file as any).type, res.agreementContentId)
          .catch(pdfErr => console.error('Lỗi sinh PDF preview:', pdfErr));
      }

      const mapped = mapResponse(res);
      console.log('uploadFileToGoogle mapped:', mapped);
      if (mapped) return mapped;
    }
    throw new Error(res?.errors || res?.error || 'Upload thất bại');
  };
  const getIconByExtension = (extOrType?: string): { icon: string; color: string } => {
    const e = (extOrType || '').toLowerCase();
    if (e === 'pdf') return { icon: 'pi pi-file-pdf', color: 'text-red-500' };
    if (
      e === 'doc' ||
      e === 'docx' ||
      e === 'application/vnd.google-apps.document' ||
      e === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
      e === 'application/msword'
    )
      return { icon: 'pi pi-file-word', color: 'text-blue-500' };
    if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(e)) return { icon: 'pi pi-image', color: 'text-green-500' };
    if (['xlsx', 'xls', 'csv'].includes(e)) return { icon: 'pi pi-file-excel', color: 'text-green-600' };
    if (['ppt', 'pptx'].includes(e)) return { icon: 'pi pi-file', color: 'text-orange-500' };
    if (['zip', 'rar'].includes(e)) return { icon: 'pi pi-file-archive', color: 'text-yellow-600' };
    if (['txt', 'json', 'xml', 'html'].includes(e)) return { icon: 'pi pi-file', color: 'text-gray-600' };
    return { icon: 'pi pi-file', color: 'text-blue-500' };
  };

  const ensureMainFile = (files: any[]) => {
    return files;
  };

  const setMainFile = (idx: number) => {
    setMainFiles(prev => prev.map((f, i) => ({ ...f, isMainFile: i === idx })));
    markDirty();
  };

  const handleCreateGoogleFile = () => {
    const tempId = `temp-gdoc-${Date.now()}`;
    const placeholder = {
      name: formData.agreementName || 'Tài liệu mới',
      size: 0,
      type: 'application/vnd.google-apps.document',
      fileId: tempId,
      id: tempId,
      uploading: true,
      extension: 'docx',
      isMainFile: true
    };

    setMainFiles(prev => [...prev, placeholder]);

    Oauth2GoogleService.create({ agreementId: agreementId, fileName: formData.agreementName || 'Tài liệu mới' })
      .then(res => {
        if (res && res.fileId) {
          const mapped = {
            name: res.fileName || formData.agreementName || 'Tài liệu mới',
            size: 0,
            type: 'application/vnd.google-apps.document',
            fileId: res.fileId,
            id: res.fileId,
            googleFileId: res.fileId,
            googleFileUrl: `https://docs.google.com/document/d/${res.fileId}/edit`,
            extension: 'docx',
            isMainFile: true
          };
          setMainFiles(prev => prev.map(f => f.fileId === tempId ? mapped : f));
          setGoogleFileId(res.fileId);
          setShowGDoc(true);
          setDisplayEditor({ fileId: res.fileId });
          markDirty();
        } else {
          ToastService.error('Không thể tạo file Google Doc');
          setMainFiles([]);
        }
      })
      .catch(err => {
        ToastService.error('Có lỗi xảy ra khi tạo file Google Doc');
        setMainFiles([]);
      });
  };
  const handleComputerUpload = () => {
    if (isViewMode) return;
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf,.doc,.docx';
    input.multiple = true;
    input.onchange = (e: any) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        const fileList = Array.from(files);
        
        fileList.forEach((file: any) => {
          const extension = getFileExtension(file.name);
          const allowedExtensions = ['pdf', 'docx', 'doc'];

          if (!allowedExtensions.includes(extension)) {
            ToastService.error(`File ${file.name} không đúng định dạng. Chỉ hỗ trợ PDF, DOCX, DOC.`);
            return;
          }

          const tempId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
          const placeholder = {
            name: file.name,
            size: file.size,
            type: file.type,
            fileId: tempId,
            id: tempId,
            uploading: true,
            extension: extension,
            isMainFile: true
          };

          setMainFiles(prev => [...prev, placeholder]);

          AgreementService.uploadFile(file)
            .then(res => {
              if (res && (res.success === 'Y' || res.fileId)) {
                const mapped = {
                  name: res.fileName || file.name,
                  size: file.size,
                  type: file.type,
                  fileId: res.fileId,
                  id: res.fileId,
                  googleFileId: res.fileId,
                  googleFileUrl: res.fileUrl,
                  extension: res.format || extension,
                  isMainFile: true,
                  referenceFileId: res.referenceFileId
                };

                setMainFiles(prev => prev.map(f => f.fileId === tempId ? mapped : f));

                // Sinh PDF preview ngầm
                const ext = (mapped.extension || '').toLowerCase();
                if ((ext === 'docx' || ext === 'doc') && !mapped.referenceFileId) {
                  setPdfGenerating(prev => new Set(prev).add(res.fileId));
                  AgreementService.generatePdf(res.fileId, mapped.name, mapped.type, res.agreementContentId)
                    .then(pdfRes => {
                      if (pdfRes?.referenceFileId) {
                        setMainFiles(prev => prev.map(f => f.fileId === res.fileId ? { ...f, referenceFileId: pdfRes.referenceFileId } : f));
                      }
                    })
                    .finally(() => {
                      setPdfGenerating(prev => {
                        const next = new Set(prev);
                        next.delete(res.fileId);
                        return next;
                      });
                    });
                }
                markDirty();
              }
            })
            .catch(err => {
              ToastService.error(`Lỗi khi tải tệp ${file.name} lên`);
              setMainFiles(prev => prev.filter(f => f.fileId !== tempId));
            });
        });
      }
    };
    input.click();
  };

  // Do not auto-download PDFs for thumbnails on step 1; step 3 prepares the PDF canvas when needed.




  const handleAttachmentUpload = () => {
    if (isViewMode) return;
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.accept = '.pdf,.doc,.docx';
    input.onchange = (e: any) => {
      const selectedFiles = Array.from(e.target.files || []) as File[];
      selectedFiles.forEach((file, index) => {
        const extension = getFileExtension(file.name);
        if (!['pdf', 'doc', 'docx'].includes(extension)) {
          ToastService.error(`File ${file.name} không đúng định dạng. Chỉ hỗ trợ PDF, DOCX, DOC.`);
          return;
        }

        const tempId = `temp-attachment-${Date.now()}-${index}`;
        setAttachedFiles(prev => [...prev, {
          name: file.name,
          size: file.size,
          type: file.type,
          fileId: tempId,
          id: tempId,
          uploading: true,
          extension,
          isMainFile: false
        }]);

        AgreementService.uploadFile(file)
          .then(async res => {
            if (!res || (res.success !== 'Y' && !res.fileId)) {
              throw new Error(res?.error || res?.errors || 'Upload thất bại');
            }
            const mapped = {
              name: res.fileName || file.name,
              size: file.size,
              type: file.type,
              fileId: res.fileId,
              id: res.fileId,
              googleFileId: res.fileId,
              googleFileUrl: res.fileUrl,
              extension: res.format || extension,
              isMainFile: false,
              referenceFileId: res.referenceFileId
            };
            setAttachedFiles(prev => prev.map(f => f.fileId === tempId ? mapped : f));

            if ((extension === 'docx' || extension === 'doc') && !mapped.referenceFileId) {
              setPdfGenerating(prev => new Set(prev).add(res.fileId));
              try {
                const pdfRes = await AgreementService.generatePdf(res.fileId, mapped.name, mapped.type, res.agreementContentId);
                if (pdfRes?.referenceFileId) {
                  setAttachedFiles(prev => prev.map(f => f.fileId === res.fileId ? { ...f, referenceFileId: pdfRes.referenceFileId } : f));
                }
              } catch {
                ToastService.error(`Không thể tạo bản xem trước cho ${mapped.name}`);
              } finally {
                setPdfGenerating(prev => {
                  const next = new Set(prev);
                  next.delete(res.fileId);
                  return next;
                });
              }
            }
            markDirty();
          })
          .catch(() => {
            ToastService.error(`Không thể tải file ${file.name}`);
            setAttachedFiles(prev => prev.filter(f => f.fileId !== tempId));
          });
      });
    };
    input.click();
  };
  const extractViewFileId = (file: any) => {
    console.log('extractViewFileId (TaiLieu) file:', file);
    return file?.referenceFileId || null;
  };

  const extractOriginalFileId = (file: any) => {
    console.log('extractOriginalFileId (TaiLieu) file:', file);
    // Bỏ qua referenceFileId vì đây là bản PDF chỉ để hiển thị
    if (file?.fileId) return file.fileId;
    if (file?.googleFileId) return file.googleFileId;

    const location = file?.contentLocation || file?.googleFileUrl;
    if (location && typeof location === 'string') {
      const match = location.match(/\/d\/([a-zA-Z0-9_-]+)/);
      if (match && match[1]) return match[1];
    }

    return file?.agreementContentId || file?.id || file?.contentId || file?.description;
  };

  const handleViewFile = async (file: any) => {
    console.log('handleViewFile (TaiLieu) click file:', file);
    let targetId = extractViewFileId(file);
    const fileId = file.fileId || file.googleFileId || file.id || file.agreementContentId;

    if (!targetId && fileId) {
      ToastService.error('Đang chuẩn bị bản xem trước, vui lòng đợi ít giây...');
      setPdfGenerating(prev => new Set(prev).add(fileId));
      try {
        const res = await AgreementService.generatePdf(
          fileId,
          file.name || file.fileName,
          file.type || file.contentType,
          file.agreementContentId
        );
        if ((res?.success === 'Y' || res?.referenceFileId) && res?.referenceFileId) {
          targetId = res.referenceFileId;
          const updateReference = (current: any) => {
            const currentId = current.fileId || current.googleFileId || current.id || current.agreementContentId;
            return String(currentId) === String(fileId) ? { ...current, referenceFileId: targetId } : current;
          };
          setMainFiles(prev => prev.map(updateReference));
          setAttachedFiles(prev => prev.map(updateReference));
        } else {
          console.error('generatePdf failed:', res);
          ToastService.error(res?.message || res?.errors || 'Không tạo được bản PDF preview');
        }
      } catch (err) {
        console.error('Lỗi khi chuẩn bị file PDF:', err);
        ToastService.error('Không tạo được bản PDF preview');
      } finally {
        setPdfGenerating(prev => {
          const next = new Set(prev);
          next.delete(fileId);
          return next;
        });
      }
    }

    if (!targetId) {
      if (toast.current) {
        toast.current.show({ severity: 'error', summary: 'Lỗi', detail: 'Không tìm thấy bản PDF phù hợp để xem. Vui lòng thử lại sau ít phút!' });
      } else {
        ToastService.error('Không tìm thấy bản PDF phù hợp để xem. Vui lòng thử lại sau ít phút!');
      }
      return;
    }
    setSelectedFile({ ...file, referenceFileId: targetId });
    setViewerUrl(targetId);
    setCurrentPage(1);
    setShowFileViewer(true);
  };

  const handleEditFile = async (file: any) => {
    console.log('>>> handleEditFile (Hybrid Storage):', { file, agreementId });
    const fileId = extractOriginalFileId(file);
    if (!fileId) {
      console.error('>>> handleEditFile error: No fileId found for', file);
      ToastService.error('File này không thể chỉnh sửa');
      return;
    }

    try {
      setFileOperationLoading(true);
      console.log('>>> Calling openEditor with:', { agreementId, fileId });
      // Gọi service openEditor để đồng bộ từ MinIO sang GD nếu cần
      const result = await AgreementService.openEditor(agreementId, fileId);
      console.log('>>> openEditor result:', result);

      if (result && result.googleFileId) {
        setGoogleFileId(result.googleFileId);
        setDisplayEditor({ 
          fileId: result.googleFileId, 
          originalFileId: fileId, // Lưu lại ID gốc trên MinIO để lưu đè sau này
          fileName: file.name || file.fileName 
        });
      } else {
        ToastService.error(result?.error || 'Không thể mở quyền chỉnh sửa');
      }
    } catch (error) {
      ToastService.error('Có lỗi xảy ra khi mở chỉnh sửa');
    } finally {
      setFileOperationLoading(false);
    }
  };

  const handleCloseFile = async (file: any) => {
    if (!file.googleFileId) {
      ToastService.error('File này không thể đóng quyền');
      return;
    }

    try {
      await AgreementService.closeContent(agreementId, file.googleFileId);
    } catch (error) {
      ToastService.error('Có lỗi xảy ra khi đóng File');
    }
  };

  useEffect(() => {
    if (!displayEditor && selectedFile?.googleFileId) {
      AgreementService.closeContent(agreementId, selectedFile.googleFileId).catch(() => undefined);
    }
  }, [displayEditor, selectedFile, agreementId]);


  const handleDownloadFile = async (file: any) => {
    try {
      const fileId = extractOriginalFileId(file);
      if (!fileId) throw new Error('missing fileId');

      const blob = await AgreementService.downloadFile(fileId, { original: true, contentLocation: file.contentLocation || file.googleFileUrl });
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;

      // Extract filename from blob (from Content-Disposition header) if available
      const blobFileName = (blob as any).fileName;

      // Priority: file.name > file.fileName > blobFileName > 'document'
      // Always use the name from the file being downloaded, not from mainFile
      const fileName = file.name || file.fileName || blobFileName || 'document';

      // Only add extension if filename doesn't already have one
      const hasExtension = fileName.includes('.') && /\.\w+$/.test(fileName);

      // Use extension from the file being downloaded
      const ext = (file.extension && !hasExtension ? ('.' + file.extension) : '') || '';

      const finalFileName = hasExtension ? fileName : (fileName + ext);

      link.download = finalFileName;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e) {
      ToastService.error('Không thể tải xuống file này');
    }
  };

  const getFileMenuItems = (file: any) => {
    const isUploading = !!file.uploading;
    const targetIdCheck = file.fileId || file.googleFileId || file.agreementContentId || file.id;
    const isPdfGenerating = pdfGenerating.has(targetIdCheck);
    const isPreparing = isUploading || isPdfGenerating;
    
    const allItems = [
      {
        label: isPdfGenerating ? 'Đang chuẩn bị...' : 'Xem',
        icon: isPdfGenerating ? 'pi pi-spin pi-spinner' : 'pi pi-eye',
        command: (e: any) => {
          e?.originalEvent?.stopPropagation?.();
          if (isPdfGenerating) {
            ToastService.error('Bản xem trước đang được xử lý, vui lòng đợi xíu nha!');
            return;
          }
          handleViewFile(file);
        },
        disabled: isPdfGenerating || isUploading
      },
      {
        label: 'Chỉnh sửa',
        icon: 'pi pi-pencil',
        command: (e: any) => {
          e?.originalEvent?.stopPropagation?.();
          handleEditFile(file);
        },
        disabled: file.extension === 'pdf' || isUploading,
        hidden: isViewMode
      },
      {
        label: 'Tải xuống',
        icon: 'pi pi-download',
        command: (e: any) => {
          e?.originalEvent?.stopPropagation?.();
          handleDownloadFile(file);
        },
        disabled: isUploading
      },
      {
        separator: true,
        hidden: isViewMode
      },
      {
        label: 'Xóa',
        icon: 'pi pi-trash',
        command: (e: any) => {
          e?.originalEvent?.stopPropagation?.();
          handleRemoveLocalFile(file);
        },
        template: (item: any, options: any) => {
          return (
            <button 
              onClick={(e) => options.onClick(e)} 
              className="p-menuitem-link appearance-none border-none bg-transparent w-full text-left flex align-items-center gap-2 px-3 py-2" 
              style={{ cursor: 'pointer', transition: 'background-color 0.2s' }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--surface-hover)')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
            >
              <span className={`p-menuitem-icon ${item.icon} text-red-500`}></span>
              <span className="p-menuitem-text text-red-500 font-medium">{item.label}</span>
            </button>
          )
        },
        hidden: isViewMode,
        disabled: isUploading
      }
    ];
    return allItems.filter(item => !item.hidden);
  };

  const toggleAttachedFiles = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsAttachedFilesExpanded(!isAttachedFilesExpanded);
  };

  const handleFinalSubmit = async () => {
    if (isSubmitting) return;
    if (attachedFiles.some(file => file.uploading) || pdfGenerating.size > 0) {
      ToastService.error('Vui lòng chờ tải file đính kèm hoàn tất');
      return;
    }
    setIsSubmitting(true);
    try {
      if (!formData.agreementName || !formData.agreementName.trim()) {
        ToastService.error('Vui lòng nhập Tên tài liệu');
        return;
      }
      if (!formData.agreementType) {
        ToastService.error('Vui lòng chọn Loại tài liệu');
        return;
      }
      const mainFile = mainFiles.find(f => f.isMainFile);
      if (!mainFile) {
        ToastService.error('Không tìm thấy file chính để lưu vị trí ký');
        return;
      }

      const findRecipientForPosition = (pos: any) => documentRecipients.find((r: any) => {
        const recipientPartyId = r.partyId || r.party?.partyId || r.party?.value;
        const recipientTemplatePartyId = r.templatePartyId || r.party?.templatePartyId || '';
        const positionTemplatePartyId = pos.templatePartyId || pos.slotId || '';
        return (pos.internalRecipientId && r._internalId && String(pos.internalRecipientId) === String(r._internalId)) ||
          (positionTemplatePartyId && recipientTemplatePartyId && String(positionTemplatePartyId) === String(recipientTemplatePartyId)) ||
          (pos.partyId && recipientPartyId && String(pos.partyId) === String(recipientPartyId));
      });
      const validSignaturePositions = signaturePositions.filter(pos => !!findRecipientForPosition(pos));

      const missingSignerPositions = documentRecipients.filter(s => {
        const sId = s.partyId || s.party?.partyId || s.party?.value;
        const sTemplateId = s.templatePartyId || s.party?.templatePartyId || '';
        return !validSignaturePositions.some(p =>
          (p.internalRecipientId && s._internalId && String(p.internalRecipientId) === String(s._internalId)) ||
          (p.templatePartyId && sTemplateId && String(p.templatePartyId) === String(sTemplateId)) ||
          (p.partyId && sId && String(p.partyId) === String(sId))
        );
      });
      if (missingSignerPositions.length > 0) {
        const missingNames = missingSignerPositions
          .map(s => s.party?.label || s.partyName || s.partyId)
          .join(', ');
        ToastService.error(`Vui lòng thêm vị trí ký cho: ${missingNames}`);
        return;
      }

      const normalizedPositions = normalizeLocationSeq(validSignaturePositions);

      const formatDateForAPI = (date: Date | null) => {
        if (!date) return undefined;
        const d = new Date(date);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        const hours = String(d.getHours()).padStart(2, '0');
        const minutes = String(d.getMinutes()).padStart(2, '0');
        const seconds = String(d.getSeconds()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
      };

      const mainFilesArray = mainFiles.map(file => ({
        agreementContentId: file.agreementContentId || undefined,
        fileName: file.name,
        fileId: file.googleFileId || file.fileId || file.id,
        referenceFileId: file.referenceFileId || undefined,
        contentType: file.type,
        contentLocation: file.googleFileUrl || file.contentLocation || undefined
      }));

      const attachmentFilesArray = attachedFiles.map(file => ({
        agreementContentId: file.agreementContentId || undefined,
        fileName: file.name || file.fileName,
        fileId: file.googleFileId || file.fileId || file.id,
        referenceFileId: file.referenceFileId || undefined,
        contentType: file.type || file.contentType,
        contentLocation: file.googleFileUrl || file.contentLocation || undefined
      }));

      // Tạo agreementParties array
      const agreementPartiesArray = documentRecipients.map((rec: any) => {
        const partyInfo = rec.party || {};
        const rawSequence = Number(rec.sequenceNum ?? rec.signingOrder);
        const sequenceNum = signingModeEnumId === 'AgtSignSequential' && Number.isFinite(rawSequence) && rawSequence > 0 ? Math.floor(rawSequence) : 1;
        return {
          partyId: rec.partyId || partyInfo.partyId || partyInfo.value,
          roleTypeId: AGREEMENT_SIGNER_ROLE,
          sequenceNum,
          statusId: rec.statusId || 'AGT_PTY_UNSIGNED',
          partyName: rec.partyName || partyInfo.partyName || partyInfo.label || '',
          emailAddress: rec.emailAddress || partyInfo.emailAddress || partyInfo.email || '',
          taxCode: rec.taxCode || partyInfo.partyTaxId || partyInfo.taxCode || '',
          contactNumber: rec.contactNumber || partyInfo.contactNumber || '',
          representativeName: rec.representativeName || partyInfo.representativeName || '',
          identityCard: rec.identityCard || partyInfo.identityCard || partyInfo.idValue || ''
        };
      });

      // Tạo signaturePositions array
      const positions = normalizedPositions.map(pos => {
        const pageIdx = Number(pos.pageIndex);
        const canvas = canvasRefs.current[pageIdx];
        const pageWidth = canvas?.width || 0;
        const pageHeight = canvas?.height || 0;
        const widthPx = Number(pos.widthPx);
        const heightPx = Number(pos.heightPx);
        const fontSize = Number(pos.fontSize);
        const x = Number(pos.x);
        const y = Number(pos.y);

        // Tìm thông tin party đầy đủ từ documentRecipients
        const recipient = findRecipientForPosition(pos);

        // Lấy toàn bộ thông tin party
        const partyInfo = recipient?.party || {};
        const partyId = recipient?.partyId || partyInfo.partyId || partyInfo.value || pos.partyId;
        // Đảm bảo fileId hợp lệ, nếu thiếu hoặc là ID tạm thời thì lấy từ file chính đang active
        let finalFileId = pos.fileId;
        if (!finalFileId || String(finalFileId).startsWith('temp-')) {
          const primaryFile = mainFiles[activeMainFileIndex] || mainFiles[0];
          finalFileId = primaryFile?.googleFileId || primaryFile?.fileId || primaryFile?.id;
        }

        return {
          locationSeqId: pos.locationSeqId,
          fileId: finalFileId,
          agreementContentId: pos.agreementContentId || undefined,
          partyId: partyId,
          roleTypeId: AGREEMENT_SIGNER_ROLE,
          signatureTypeEnumId: pos.signatureTypeEnumId,
          pageNumber: pageIdx + 1,
          pageWidth: pageWidth,
          pageHeight: pageHeight,
          widthPx: widthPx,
          heightPx: heightPx,
          ulx: x,
          uly: y,
          fontSize: fontSize,
          partyName: recipient?.partyName || pos.partyName || partyInfo.partyName || '',
          identityCard: recipient?.identityCard || partyInfo.idValue || partyInfo.identityCard || '',
          emailAddress: recipient?.emailAddress || partyInfo.emailAddress || '',
          taxCode: recipient?.taxCode || partyInfo.partyTaxId || partyInfo.taxCode || '',
          contactNumber: recipient?.contactNumber || partyInfo.contactNumber || '',
          representativeName: recipient?.representativeName || partyInfo.representativeName || ''
        };
      });

      const signatureData = {
        agreementName: formData.agreementName,
        agreementNo: formData.agreementNo || undefined,
        agreementNoInt: formData.agreementNoInt || undefined,
        agreementNoFormatEnumId: formData.agreementNoFormatEnumId || undefined,
        agreementConfigureEnumId: formData.agreementType,
        description: formData.description || undefined,
        fromDate: formatDateForAPI(formData.effectiveDate as Date | null),
        thruDate: formatDateForAPI(formData.expiryDate as Date | null),
        isImportant: formData.isImportant ? 'Y' : 'N',
        isUrgent: formData.isUrgent ? 'Y' : 'N',
        signingModeEnumId,
        mainFiles: mainFilesArray,
        attachmentFiles: attachmentFilesArray,
        agreementParties: agreementPartiesArray,
        signaturePositions: positions
      };
      const res = await AgreementService.saveSignaturePositions(agreementId as string, signatureData);
      if (res && res.success === 'Y') {
        ToastService.success('Tài liệu đã được tạo thành công!');
        setShowSignaturePlacement(false);
        try {
          if (typeof window !== 'undefined' && window.localStorage) {
            window.localStorage.setItem('econtract.taiLieuKy.activeTab', '0');
          }
        } catch (err) { }
        navigate('/quan-ly-tai-lieu/danh-sach-tai-lieu');
      } else {
        ToastService.error(res?.errorMessage || 'Không thể lưu vị trí ký');
      }
    } catch (e) {
      ToastService.error('Có lỗi xảy ra khi lưu vị trí ký');
    } finally {
      setIsSubmitting(false);
    }
  };

  const generateDocumentCode = (agreementTypeOverride?: string | null) => {

    const agreementType = agreementTypeOverride ?? formData.agreementType;
    if (!agreementType) {
      ToastService.error('Vui lòng chọn Loại tài liệu trước');
      return;
    }
    // Lưu agreementType vào closure để đảm bảo giữ lại khi update formData
    const savedAgreementType = agreementType;
    AgreementService.generateDocumentCode(agreementType)
      .then(async (res: any) => {
        if (res && res.success === 'Y') {
          // Cập nhật agreementNo và agreementNoInt cùng lúc, đảm bảo giữ lại agreementType
          setFormData(prev => ({
            ...prev,
            agreementType: prev.agreementType || savedAgreementType, // Giữ lại agreementType nếu đã có, nếu không thì dùng savedAgreementType
            agreementNo: res.agreementNo,
            agreementNoInt: typeof res.agreementNoInt === 'number' ? res.agreementNoInt : null,
            agreementNoFormatEnumId: res.agreementNoFormatEnumId || null
          }));

          if (res.templateAgreementId) {
            setTemplateAgreementId(res.templateAgreementId);
            // Không tự động bật isTemplateMode ở đây để tránh khóa dropdown Loại tài liệu khi hệ thống chỉ gợi ý mẫu
          } else {
            setTemplateAgreementId(null);
          }

          // Tự động load thông tin từ mẫu tài liệu
          if (res.templateAgreementId && isTemplateMode) {
            try {
              const detail = await AgreementTemplateService.get(res.templateAgreementId);
              if (detail) {
                // 1. Set main file
                const templateFileId = detail.fileId || detail.googleFileId;
                if (templateFileId) {
                  const fileName = detail.fileName || 'Tài liệu từ mẫu';
                  const contentType = detail.contentType || 'application/vnd.google-apps.document';
                  const extension = extractFileExtension(fileName, contentType);

                  setMainFiles([{
                    name: fileName,
                    googleFileId: templateFileId,
                    fileId: templateFileId,
                    id: templateFileId,
                    googleFileUrl: `https://docs.google.com/document/d/${templateFileId}/edit`,
                    type: contentType,
                    extension: extension,
                    isMainFile: true
                  }]);
                }

                // 2. Set attachment files
                const attFiles = detail.attachmentFiles || [];
                if (Array.isArray(attFiles) && attFiles.length > 0) {
                  setAttachedFiles(attFiles.map((att: any) => ({
                    name: att.fileName || 'Tệp đính kèm',
                    fileId: att.fileId,
                    id: att.fileId,
                    contentType: att.contentType,
                    isMainFile: false
                  })));
                }

                // 3. Set recipients từ signer cụ thể của mẫu. Bỏ dòng thiếu đối tượng để tránh sinh placeholder [Mới] - từ dữ liệu mẫu cũ.
                const templateParties = detail.agreementParties || detail.roles || [];
                const recipients = Array.isArray(templateParties) ? templateParties
                  .filter((party: any) => {
                    const nameFields = getRecipientNameFields(party);
                    const hasPartyId = !!party.partyId || !!party.party?.partyId || !!party.party?.value;
                    const hasKnownName = !!(party.partyName || party.localName || nameFields.partyName || nameFields.representativeName);
                    const hasContact = !!(party.emailAddress || party.email);
                    return hasPartyId || (hasKnownName && hasContact);
                  })
                  .map((party: any, index: number) => {
                    const nameFields = getRecipientNameFields(party);
                    const templatePartyId = party.slotId || party.templatePartyId || party.party?.slotId || party.party?.templatePartyId || party.partyId || party.party?.partyId || party.party?.value || '';
                    const isUnknownRecipient = party.recipientInfoStatus === 'UNKNOWN' || !!party.slotId || String(templatePartyId).startsWith('TPL_SLOT_');
                    const partyId = isUnknownRecipient ? '' : templatePartyId;
                    const displayName = party.partyName || party.localName || nameFields.partyName || nameFields.representativeName || partyId;
                    return {
                      _internalId: crypto.randomUUID(),
                      party: isUnknownRecipient ? null : (party.party || (partyId ? { value: partyId, partyId, label: displayName } : null)),
                      partyId,
                      templatePartyId,
                      recipientInfoStatus: isUnknownRecipient ? 'UNKNOWN' : 'KNOWN',
                      taxCode: getRecipientTaxCode(party),
                      partyName: isUnknownRecipient ? displayName : nameFields.partyName,
                      emailAddress: isUnknownRecipient ? '' : (party.emailAddress || party.email || ''),
                      contactNumber: party.contactNumber || party.phone || '',
                      representativeName: nameFields.representativeName,
                      identityCard: getRecipientIdentityCard(party),
                      partyTypeEnumId: party.partyTypeEnumId,
                      roleTypeId: AGREEMENT_SIGNER_ROLE,
                      sourceRoleTypeId: party.roleTypeId || party.sourceRoleTypeId || '',
                      sequenceNum: Number(party.sequenceNum ?? party.signingOrder) || index + 1,
                      signingOrder: Number(party.sequenceNum ?? party.signingOrder) || index + 1
                    };
                  }) : [];

                if (recipients.length > 0) {
                  setDocumentRecipients(recipients);
                }

                // 4. Set signature positions từ mẫu
                const positions = detail.signaturePositions || [];
                if (Array.isArray(positions) && positions.length > 0) {
                  const mapped = positions
                    .map((pos: any, idx: number) => {
                      const positionTemplatePartyId = pos.slotId || pos.templatePartyId || pos.partyId || '';
                      const recipient = recipients.find((r: any) =>
                        positionTemplatePartyId && (
                          (r.templatePartyId && String(r.templatePartyId) === String(positionTemplatePartyId)) ||
                          (r.partyId && String(r.partyId) === String(positionTemplatePartyId))
                        )
                      );
                      if (!recipient) return null;

                      const label = recipient.partyName || pos.partyName || positionTemplatePartyId || '';

                      return {
                        locationSeqId: pos.locationSeqId || formatSeq(idx + 1),
                        internalRecipientId: recipient._internalId,
                        partyId: recipient.partyId,
                        templatePartyId: positionTemplatePartyId,
                        x: Number(pos.ulx ?? pos.llx ?? pos.x ?? 0),
                        y: Number(pos.uly ?? pos.lly ?? pos.y ?? 0),
                        pageIndex: Number(pos.pageNumber ?? pos.pageIndex ?? 1) - 1,
                        widthPx: Number(pos.widthPx ?? 200),
                        heightPx: Number(pos.heightPx ?? (pos.signatureTypeEnumId === 'AgtDigitalVerified' ? 65 : 100)),
                        fontSize: Number(pos.fontSize ?? 12),
                        signatureTypeEnumId: pos.signatureTypeEnumId,
                        roleTypeId: AGREEMENT_SIGNER_ROLE,
                        sourceRoleTypeId: pos.roleTypeId || '',
                        partyName: label,
                        label: label,
                        isTemplatePosition: true,
                        isUnmatchedTemplatePosition: !recipient.partyId
                      };
                    })
                    .filter(Boolean);
                  setSignaturePositions(normalizeLocationSeq(mapped));
                }
              }
            } catch (err) {
              console.error('Error loading template data', err);
            }
          }
        } else {
          ToastService.error(res?.error || 'Không tạo được mã tài liệu');
        }
      })
      .catch(() => {
        ToastService.error('Không tạo được mã tài liệu');
      });
  };

  const handleSubmit = async () => {
    if (!isViewMode) {
      setValidationTriggered(true);
      if (!validateForm()) {
        return;
      }
    }
    await loadStep2Data();
    setShowRecipients(true);
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const getCurrentStep = () => {
    if (showSignaturePlacement) return 3;
    if (showRecipients) return 2;
    return 1;
  };

  const renderStepIndicators = (expand: boolean) => {
    const currentStep = getCurrentStep();
    const connectorClass = expand ? 'flex-1 border-top-1' : 'border-top-1';
    const connectorStyle = {
      width: expand ? undefined : '80px',
      maxWidth: '120px',
      height: '2px',
      margin: '0 8px'
    } as React.CSSProperties;

    return (
      <div className={`flex align-items-center justify-content-center ${expand ? 'flex-1' : ''}`} style={{ gap: '12px' }}>
        {/* Step 1 */}
        <div className="flex align-items-center" style={{ gap: '8px' }}>
          <div
            className={`flex align-items-center justify-content-center border-circle ${currentStep >= 1 ? 'bg-primary text-white' : 'border-2 text-400'}`}
            style={{
              width: '32px',
              height: '32px',
              fontSize: '14px',
              fontWeight: '600',
              minWidth: '32px',
              backgroundColor: currentStep >= 1 ? 'var(--primary-color)' : 'var(--surface-card)',
              borderColor: currentStep >= 1 ? 'var(--primary-color)' : 'var(--surface-border)',
              color: currentStep >= 1 ? 'var(--primary-color-text)' : 'var(--text-color-secondary)'
            }}
          >
            1
          </div>
          <span
            className={`text-sm font-medium`}
            style={{ whiteSpace: 'nowrap', color: currentStep >= 1 ? 'var(--text-color)' : 'var(--text-color-secondary)' }}
          >
            Tải lên tài liệu
          </span>
        </div>

        {/* Connector Line */}
        <div
          className={`${connectorClass} ${currentStep >= 2 ? 'border-blue-500' : 'border-gray-300'}`}
          style={connectorStyle}
        ></div>

        {/* Step 2 */}
        <div className="flex align-items-center" style={{ gap: '8px' }}>
          <div
            className={`flex align-items-center justify-content-center border-circle ${currentStep >= 2 ? 'bg-primary text-white' : 'border-2 text-400'}`}
            style={{
              width: '32px',
              height: '32px',
              fontSize: '14px',
              fontWeight: '600',
              minWidth: '32px',
              backgroundColor: currentStep >= 2 ? 'var(--primary-color)' : 'var(--surface-card)',
              borderColor: currentStep >= 2 ? 'var(--primary-color)' : 'var(--surface-border)',
              color: currentStep >= 2 ? 'var(--primary-color-text)' : 'var(--text-color-secondary)'
            }}
          >
            2
          </div>
          <span
            className={`text-sm font-medium`}
            style={{ whiteSpace: 'nowrap', color: currentStep >= 2 ? 'var(--text-color)' : 'var(--text-color-secondary)' }}
          >
            Thiết lập người nhận
          </span>
        </div>

        {/* Connector Line */}
        <div
          className={`${connectorClass} ${currentStep >= 3 ? 'border-blue-500' : 'border-gray-300'}`}
          style={connectorStyle}
        ></div>

        {/* Step 3 */}
        <div className="flex align-items-center" style={{ gap: '8px' }}>
          <div
            className={`flex align-items-center justify-content-center border-circle ${currentStep >= 3 ? 'bg-primary text-white' : 'border-2 text-400'}`}
            style={{
              width: '32px',
              height: '32px',
              fontSize: '14px',
              fontWeight: '600',
              minWidth: '32px',
              backgroundColor: currentStep >= 3 ? 'var(--primary-color)' : 'var(--surface-card)',
              borderColor: currentStep >= 3 ? 'var(--primary-color)' : 'var(--surface-border)',
              color: currentStep >= 3 ? 'var(--primary-color-text)' : 'var(--text-color-secondary)'
            }}
          >
            3
          </div>
          <span
            className={`text-sm font-medium`}
            style={{ whiteSpace: 'nowrap', color: currentStep >= 3 ? 'var(--text-color)' : 'var(--text-color-secondary)' }}
          >
            Thiết lập vị trí ký
          </span>
        </div>
      </div>
    );
  };

  // Component render progress steps (có thể có hoặc không có nút đóng)
  const renderProgressSteps = (showButtons: boolean = true, overrides?: { onNext?: () => void; onBack?: () => void; nextLabel?: string; backLabel?: string; showNext?: boolean; showBack?: boolean }) => {
    return (
      <div
        className={`px-4 py-3 border-bottom-1 relative`}
        style={{
          backgroundColor: 'var(--surface-card)',
          borderColor: 'var(--surface-border)',
          zIndex: 10
        }}
      >
        <div
          className="flex align-items-center justify-content-center"
          style={{ gap: '12px' }}
        >
          {renderStepIndicators(showButtons)}
        </div>
        {showButtons && (
          <Button
            icon="pi pi-times"
            className="p-button-text p-button-secondary absolute"
            style={{ right: '1rem', top: '50%', transform: 'translateY(-50%)' }}
            onClick={overrides?.onBack || handleCancel}
            aria-label="Đóng"
          />
        )}
      </div>
    );
  };

  const renderMainFooter = () => (
    <div
      className="px-4 py-3 border-top-1 flex align-items-center justify-content-between"
      style={{
        backgroundColor: 'var(--surface-card)',
        borderColor: 'var(--surface-border)'
      }}
    >
      <span />
      <Button
        label="Tiếp tục"
        icon="pi pi-arrow-right"
        iconPos="right"
        onClick={handleSubmit}
        className="p-button-success"
      />
    </div>
  );

  const renderStep2Footer = () => (
    <div
      className="px-4 py-3 border-top-1 flex align-items-center justify-content-between"
      style={{
        backgroundColor: 'var(--surface-card)',
        borderColor: 'var(--surface-border)'
      }}
    >
      <Button
        label="Quay lại"
        icon="pi pi-arrow-left"
        className="p-button-outlined p-button-secondary"
        onClick={() => setShowRecipients(false)}
      />
      <Button
        label="Tiếp theo"
        icon="pi pi-arrow-right"
        iconPos="right"
        onClick={() => {
          if (validateRecipients()) {
            setShowRecipients(false);
            setShowSignaturePlacement(true);
          }
        }}
      />
    </div>
  );

  const renderStep3Footer = () => (
    <div
      className="px-4 py-3 border-top-1 flex align-items-center justify-content-between"
      style={{
        backgroundColor: 'var(--surface-card)',
        borderColor: 'var(--surface-border)'
      }}
    >
      <Button
        label="Quay lại"
        icon="pi pi-arrow-left"
        className="p-button-outlined p-button-secondary"
        onClick={() => {
          setShowSignaturePlacement(false);
          setShowRecipients(true);
        }}
      />
      {!isViewMode ? (
        <Button
          label="Hoàn thành"
          icon="pi pi-check"
          iconPos="right"
          severity="success"
          loading={isSubmitting}
          disabled={isSubmitting}
          onClick={handleFinalSubmit}
        />
      ) : <span />}
    </div>
  );

  return (
    <div
      className={`min-h-screen ${!isViewMode ? 'fixed top-0 left-0 w-full h-full overflow-y-auto' : ''}`}
      style={{
        backgroundColor: 'var(--surface-ground)',
        ...(!isViewMode ? { zIndex: 1000 } : {})
      }}
    >
      {initializing && <div className="p-4" style={{ color: 'var(--text-color)' }}>Đang tải...</div>}


      {/* Progress Steps */}
      {renderProgressSteps(true)}

      {/* Main Content */}
      {!showRecipients && !showSignaturePlacement && (
      <div className="p-3">
        <Step1InfoAndFiles
          formData={formData}
          setFormData={setFormData}
          documentTypes={documentTypes}
          isViewMode={isViewMode}
          validationTriggered={validationTriggered}
          touchedFields={touchedFields}
          markDirty={markDirty}
          mainFiles={mainFiles}
          attachedFiles={attachedFiles}
          filePreviews={filePreviews}
          previewLoading={previewLoading}
          onComputerUpload={handleComputerUpload}
          onCreateGoogleFile={handleCreateGoogleFile}
          onViewFile={handleViewFile}
          onRemoveFile={handleRemoveLocalFile}
          onAttachmentUpload={handleAttachmentUpload}
          getFileMenuItems={getFileMenuItems}
          getAttachmentMenuItems={getFileMenuItems}
          getIconByExtension={getIconByExtension}
          isTemplateMode={isTemplateMode}
          pdfGenerating={pdfGenerating}
        />
      </div>
      )}
      {!showRecipients && !showSignaturePlacement && renderMainFooter()}

      {showRecipients && (
        <>
          <div className="p-4" style={{ minHeight: 'calc(100vh - 145px)' }}>
            <Step2Signers
              documentRecipients={documentRecipients}
              roleOptions={roleOptions}
              isViewMode={isViewMode}
              getCustomerSuggestionsForRow={getCustomerSuggestionsForRow}
              searchCustomers={searchCustomers}
              createCustomer={createCustomer}
              handleRecipientChange={handleRecipientChange}
              setDocumentRecipients={setDocumentRecipients}
              addRecipient={addRecipient}
              removeRecipient={removeRecipient}
              getRoleOptionsForRow={getRoleOptionsForRow}
              isTemplateMode={isTemplateMode || isFromTemplateAgreement}
              isSequential={signingModeEnumId === 'AgtSignSequential'}
              signingMode={signingModeEnumId}
              onSigningModeChange={setSigningModeEnumId}
            />
          </div>
          {renderStep2Footer()}
        </>
      )}

      {showSignaturePlacement && (
        <>
          <div className="p-0" style={{ height: 'calc(100vh - 145px)', minHeight: '640px', overflow: 'hidden' }}>
            <Step3Positions
              documentRecipients={documentRecipients}
              signaturePositions={signaturePositions}
              selectedSigner={selectedSigner}
              setSelectedSigner={setSelectedSigner}
              canvasLoading={canvasLoading}
              pageCanvases={pageCanvases}
              canvasRefs={canvasRefs}
              draggableRefs={draggableRefs}
              handleDrop={handleDrop}
              handleDragStop={handleDragStop}
              removeSignaturePosition={removeSignaturePosition}
              setSignaturePositions={setSignaturePositions}
              normalizeLocationSeq={normalizeLocationSeq}
              isTemplateMode={isTemplateMode || isFromTemplateAgreement}
              selectedSignatureType={selectedSignatureType}
              setSelectedSignatureType={setSelectedSignatureType}
              mainFiles={mainFiles}
              activeMainFileIndex={activeMainFileIndex}
              onSetActiveFile={setActiveMainFileIndex}
              enableZoom
            />
          </div>
          {renderStep3Footer()}
        </>
      )}
      {/* File Viewer Dialog */}
      <Dialog
        header={
          <div className="flex justify-content-between align-items-center w-full">
            <span className="font-semibold text-lg">{selectedFile?.name || selectedFile?.fileName || 'Xem tài liệu'}</span>
          </div>
        }
        visible={showFileViewer}
        style={{ width: '98vw', height: '95vh', maxWidth: 'none' }}
        contentClassName="p-0 flex flex-column"
        onHide={() => {
          setShowFileViewer(false);
          setViewerUrl(null);
          setSelectedFile(null);
        }}
        footer={
          <div className="flex justify-content-end">
            <Button
              icon="pi pi-times"
              label="Đóng"
              className="p-button-outlined p-button-secondary p-button-sm"
              onClick={() => setShowFileViewer(false)}
            />
          </div>
        }
      >
        {viewerUrl && (
          <PdfCanvasViewer
            fileId={viewerUrl}
            fileName={selectedFile?.name || selectedFile?.fileName || 'document.pdf'}
            scale={2.0}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
          />
        )}
      </Dialog>

      <EditorDialog
        display={displayEditor}
        setDisplay={setDisplayEditor}
        agreementId={agreementId}
        showRecipients={showRecipients}
        setShowRecipients={setShowRecipients}
        validateRecipients={validateRecipients}
        setMainFiles={setMainFiles}
        setAttachedFiles={setAttachedFiles}
      />
      {customerDialog.render()}
      {/* Leave Page Confirm Dialog */}
      <Dialog
        header="Xác nhận rời trang"
        visible={showLeavePrompt}
        style={{ width: '32rem' }}
        onHide={() => setShowLeavePrompt(false)}
        footer={(
          <div className="flex justify-content-end gap-2">
            <Button
              label="Ở lại trang"
              icon="pi pi-times"
              className="p-button-outlined p-button-secondary p-button-sm"
              onClick={() => setShowLeavePrompt(false)}
            />
            <Button
              label="Rời trang"
              icon="pi pi-check"
              className="p-button-danger p-button-sm"
              onClick={() => {
                setShowLeavePrompt(false);
                setHasUnsavedChanges(false);
                if (pendingHash) {
                  window.location.hash = pendingHash;
                }
              }}
            />
          </div>
        )}
      >
        <div className="p-2">
          Bạn có thay đổi chưa lưu. Rời trang sẽ mất các thay đổi. Tiếp tục?
        </div>
      </Dialog>
      {fileOperationLoading && (
        <div
          className="fixed top-0 left-0 w-full h-full flex align-items-center justify-content-center"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.4)', zIndex: 1500 }}
        >
          <div className="surface-card p-4 border-round flex align-items-center gap-3 shadow-5">
            <i className="pi pi-spin pi-spinner text-2xl text-primary"></i>
            <span className="text-lg font-semibold">Đang xử lý tệp, vui lòng đợi...</span>
          </div>
        </div>
      )}
      {customerDialog.render()}
    </div>
  );
};
const EditorDialog = ({ display, setDisplay, agreementId, showRecipients, setShowRecipients, validateRecipients, setMainFiles, setAttachedFiles }: any) => {
  const [fileId, setFileId] = useState<any>(false);
  const [originalFileId, setOriginalFileId] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string>('');

  React.useEffect(() => {
    if (display?.fileId) {
      setFileId(display.fileId);
      setOriginalFileId(display.originalFileId || null);
      if (display.fileName) {
        setFileName(display.fileName);
      }
    }
  }, [display]);

  const handleSaveFile = async () => {
    console.log('>>> handleSaveFile (Hybrid Storage):', { fileId, originalFileId, agreementId, fileName });
    if (!fileId) return;
    if (!fileName || !fileName.trim()) {
      ToastService.error('Vui lòng nhập tên tài liệu');
      return;
    }

    try {
      let res;
      if (originalFileId) {
        console.log('>>> Calling saveEditor (Sync GD to MinIO):', { agreementId, originalFileId, fileId });
        // Luồng Hybrid Storage: Đồng bộ từ GD (fileId) về MinIO (originalFileId)
        res = await AgreementService.saveEditor(agreementId, originalFileId, fileId);
      } else {
        console.log('>>> Calling syncEditorToMinio (GD -> MinIO):', { fileId, fileName });
        // Luồng mới: Đồng bộ từ GD về MinIO ngay khi tạo mới trên GD
        res = await AgreementService.syncEditorToMinio(fileId, fileName.trim(), display.contentType || 'application/vnd.google-apps.document');
        if (res && res.success === 'Y') {
          // Cập nhật lại originalFileId bằng ID từ MinIO để các lần lưu sau dùng saveEditor
          setOriginalFileId(res.fileId);
        }
      }
      console.log('>>> Save result:', res);

      if (res && res.success === 'Y') {
        const finalId = res.fileId || originalFileId || fileId;
        const mappedMain = ({
          name: fileName.trim(),
          size: 0,
          type: extractFileExtension(fileName.trim(), display.contentType || 'docx'),
          file: undefined,
          extension: 'docx',
          googleFileId: finalId,
          fileId: finalId,
          id: finalId,
          googleFileUrl: res.contentLocation || `https://docs.google.com/document/d/${fileId}/edit`,
          contentLocation: res.contentLocation,
          referenceFileId: res.referenceFileId || undefined,
          isMainFile: true
        });
        setMainFiles(prev => [...prev, mappedMain]);
        setTimeout(() => setDisplay(false), 0);
        ToastService.success(`Tài liệu đã được lưu thành công!`);
      } else {
        ToastService.error(res?.error || 'Không thể lưu tài liệu');
      }
    } catch (e: any) {
      ToastService.error('Error saving file');
    }
  }
  return (
    <Dialog
      header="Editor"
      visible={!!display}
      style={{ width: '98vw' }}
      position="top"
      onHide={() => setDisplay(false)}
      footer={(
        <div className="flex justify-content-end gap-2">
          <span className="p-inputgroup" style={{ maxWidth: 360 }}>
            <span className="p-inputgroup-addon"><i className="pi pi-file" /></span>
            <InputText value={fileName} onChange={(e) => setFileName(e.target.value)} placeholder="Nhập tên tài liệu" />
          </span>
          <Button label="Lưu tài liệu" icon="pi pi-save" type="button" severity="success" size="small" onClick={() => handleSaveFile()} />
          <Button label="Đóng" icon="pi pi-times" type="button" outlined severity="secondary" size="small" onClick={() => setDisplay(false)} />
        </div>
      )}
    >
      {fileId && (
        <iframe
          src={`https://docs.google.com/document/d/${fileId}/edit`}
          width="100%"
          height="800"
          style={{ border: 'none' }}
          referrerPolicy="no-referrer"
        ></iframe>
      )}
    </Dialog>
  );
};
